import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import {
  RocketLaunchIcon,
  ArchiveBoxIcon,
  PencilIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  SignalIcon,
  LockClosedIcon,
  ServerIcon,
  BoltIcon,
  CodeBracketIcon,
  GlobeAltIcon,
  MagnifyingGlassIcon,
  KeyIcon,
  ClipboardDocumentIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon, CheckIcon } from '@heroicons/react/24/solid';
import BrandLogo from '../components/BrandLogo';

import QuotaErrorModal from '../components/QuotaErrorModal';
import CronScheduleBuilder from '../components/CronScheduleBuilder';
import EnvironmentVariablesEditor, { EnvironmentVariable } from '../components/EnvironmentVariablesEditor';
import SensitiveVariablesEditor, { SensitiveVariable } from '../components/SensitiveVariablesEditor';
import apiService from '../services/api';
import { Project, Environment, BuiltImage, KeycloakEligibleDatabase, KeycloakAdminCredentials, ManagedDatabaseCredentials } from '../types/api';
import { useForm, useFieldArray } from 'react-hook-form';
import Logger from '../utils/logger';
import { showErrorToast, showSuccessToast, parseCommand } from '../utils';
import { SuccessMessages, ErrorMessages } from '../constants/messages';
import { PG_IDENTIFIER_RE, isValidPgPassword } from '../utils/pgIdentifier';
import { useTranslation } from 'react-i18next';

interface DeploymentForm {
  project_id: string;
  environment_id: string;
  app_name: string;
  template_type: string;
  admin_password?: string;
  custom_domain?: string;
  cpu: string;
  ram: string;
  storage: string;
  replicas: string;
  // Champs spécifiques aux apps CODE
  docker_image_url?: string;
  docker_image_tag?: string;
  // Champs spécifiques à Odoo
  odoo_email?: string;
  odoo_password?: string;
  postgres_password?: string;
  image_tag?: string;
  odoo_language?: string;
  // Champs spécifiques à WordPress
  wordpress_email?: string;
  wordpress_password?: string;
  mariadb_root_password?: string;
  mariadb_user_password?: string;
  // Champs spécifiques aux services managés
  root_password?: string;
  database?: string;
  username?: string;
  password?: string;
  enable_backups?: boolean;
  // Keycloak uniquement : PostgreSQL manage qui hebergera sa base.
  database_deployment_id?: string;
  // PostgreSQL uniquement: bases additionnelles (chart nubi-postgres >= 1.2.0)
  additional_databases?: Array<{
    name: string;
    user?: string;
    password?: string;
  }>;
}

// Template type for deployment templates
interface DeploymentTemplate {
  title: string;
  subtitle?: string;
  description: string;
  color: string;
  textColor: string;
  borderColor: string;
  type: string;
  icon: React.ReactNode;
  defaultConfig?: {
    cpu: number;
    ram: number;
    storage: number;
    version?: string;
  };
}

// Quota error type
interface QuotaErrorInfo {
  message: string;
  quota_exceeded: string[];
  recommendations: (string | QuotaRecommendation)[];
  quota_details: QuotaDetails;
}

interface QuotaRecommendation {
  type?: string;
  resource?: string;
  current_quota?: number | string;
  needed_increase?: number | string;
  suggested_quota?: number | string;
}

interface QuotaDetails {
  quota_cpu?: number;
  cpu_quota?: number;
  used_cpu?: number;
  cpu_used?: number;
  quota_ram?: number;
  ram_quota?: number;
  used_ram?: number;
  ram_used?: number;
  quota_stockage?: number;
  storage_quota?: number;
  used_stockage?: number;
  storage_used?: number;
}

// SaaS configuration type
interface SaaSConfiguration {
  cpu: number;
  ram: number;
  image: string;
  tag: string;
  helm_version: string;
  helm_values: {
    service: {
      type: string;
      port: number;
    };
  };
  database_config: {
    engine: string;
    database: string;
    user: string;
  };
}

/** Une ligne « libellé + valeur + copier » de l'écran de restitution des
 *  identifiants. Partagée entre le compte administrateur et la base dédiée :
 *  deux secrets, une seule présentation. */
const CredentialRow: React.FC<{ label: string; value: string; copyLabel: string }> = ({ label, value, copyLabel }) => (
  <div>
    <label className="block text-xs font-medium text-text-secondary mb-1">{label}</label>
    <div className="flex items-center gap-2">
      <code className="flex-1 px-3 py-2 bg-surface-0 border border-border-1 rounded-md text-sm text-text-primary break-all">
        {value}
      </code>
      <button
        type="button"
        onClick={() => navigator.clipboard?.writeText(value)}
        title={copyLabel}
        aria-label={copyLabel}
        className="p-2 rounded-md border border-border-1 text-text-secondary hover:text-text-primary hover:bg-surface-3 transition-colors"
      >
        <ClipboardDocumentIcon className="w-4 h-4" />
      </button>
    </div>
  </div>
);

const DeploymentPage: React.FC = () => {
  // (useTranslation as any) : neutralise TS2589 (react-i18next 14 + gros fichier) à la source
  const { t } = (useTranslation as any)('deployments');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const environmentIdFromUrl = searchParams.get('environmentId');
  
  // 3-step wizard: 1=Type & Template, 2=Environnement, 3=Configuration & Deploiement
  const [step, setStep] = useState(1);
  const [projects, setProjects] = useState<Project[]>([]);
  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [loading, setLoading] = useState(true);
  const [deploying, setDeploying] = useState(false);
  const [deploymentSuccess, setDeploymentSuccess] = useState(false);
  // Keycloak : PostgreSQL hotes candidats, et identifiants admin restitues une
  // seule fois a la creation.
  const [keycloakDatabases, setKeycloakDatabases] = useState<KeycloakEligibleDatabase[]>([]);
  const [loadingKeycloakDatabases, setLoadingKeycloakDatabases] = useState(false);
  // Distinct de la liste vide : « la requete a echoue » et « il n'y a pas de
  // base » demandent deux messages opposes. Les confondre a coute une heure de
  // diagnostic le 2026-08-30 — l'API renvoyait 500 et la console affichait
  // « creez-en une d'abord ».
  const [keycloakDatabasesError, setKeycloakDatabasesError] = useState(false);
  const [keycloakCredentials, setKeycloakCredentials] = useState<KeycloakAdminCredentials | null>(null);
  // Base dediee d'une application managee : restituee UNE FOIS, avec l'admin.
  const [databaseCredentials, setDatabaseCredentials] = useState<ManagedDatabaseCredentials | null>(null);
  const [credentialsAcknowledged, setCredentialsAcknowledged] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<DeploymentTemplate | null>(null);
  const [appType, setAppType] = useState<'code' | 'saas' | 'managed' | null>(null);
  const [quotaError, setQuotaError] = useState<QuotaErrorInfo | null>(null);
  const [showQuotaError, setShowQuotaError] = useState(false);

  // Step 1 sub-state: type selection first, then template selection
  const [templateSubStep, setTemplateSubStep] = useState<'type' | 'template'>('type');

  // Template filter/search for code apps
  const [templateFilter, setTemplateFilter] = useState<'all' | 'backend' | 'frontend'>('all');
  const [templateSearch, setTemplateSearch] = useState('');

  // États pour les variables d'environnement (non-sensibles)
  const [environmentVariables, setEnvironmentVariables] = useState<EnvironmentVariable[]>([]);

  // États pour les variables sensibles (secrets)
  const [sensitiveVariables, setSensitiveVariables] = useState<SensitiveVariable[]>([]);

  // État pour l'exposition publique sur Internet
  const [exposePublic, setExposePublic] = useState<boolean>(true);

  // États pour la migration (CODE apps)
  const [migrationEnabled, setMigrationEnabled] = useState<boolean>(false);
  const [migrationCommand, setMigrationCommand] = useState<string>('');
  const [migrationBlockOnFailure, setMigrationBlockOnFailure] = useState<boolean>(true);
  // États pour l'image dédiée à la migration (optionnelle)
  const [migrationUseCustomImage, setMigrationUseCustomImage] = useState<boolean>(false);
  const [migrationImageSourceMode, setMigrationImageSourceMode] = useState<'builtin' | 'manual'>('builtin');
  const [migrationSelectedBuiltImageId, setMigrationSelectedBuiltImageId] = useState<string | null>(null);
  const [migrationImageUrl, setMigrationImageUrl] = useState<string>('');
  const [migrationImageTag, setMigrationImageTag] = useState<string>('latest');

  // État pour le port de l'application (CODE apps)
  const [appPort, setAppPort] = useState<number>(8000);

  // Type de charge (CODE apps) : web (défaut) | worker (tâche de fond) | cron (planifié)
  const [workloadType, setWorkloadType] = useState<'web' | 'worker' | 'cron'>('web');
  const [cronSchedule, setCronSchedule] = useState<string>('*/15 * * * *');
  /* COMMANDE DE DEMARRAGE, saisie en une ligne et envoyee en LISTE (forme exec).
     La forme exec met le processus en PID 1, donc il RECOIT SIGTERM : un worker Celery qui
     ne le recoit pas n'arrete pas ses taches proprement et les perd -- exactement ce qu'on
     cherche a eviter en passant un traitement en asynchrone. La forme `sh -c` que l'API
     accepte aussi laisserait le shell en PID 1. */
  const [startCommand, setStartCommand] = useState<string>('');

  // Extensions PostgreSQL (managé) : postgis, pgvector
  const [pgExtensions, setPgExtensions] = useState<string[]>([]);
  const togglePgExtension = (ext: string) =>
    setPgExtensions((prev) => (prev.includes(ext) ? prev.filter((e) => e !== ext) : [...prev, ext]));

  // État pour le health check path (CODE apps)
  // Vide par défaut = TCP check (vérifie juste que le port est ouvert)
  const [healthCheckPath, setHealthCheckPath] = useState<string>('');

  // Config nginx.conf custom (chart nubi-code-app >= 0.5.0).
  // Vide = utilise la config auto-générée par le chart (SPA fallback pour html-static,
  // proxy vers app pour Next.js sidecar). Utilisable pour html-static + frameworks
  // frontend avec sidecar nginx (React/Next.js/Vue).
  const [nginxCustomConfig, setNginxCustomConfig] = useState<string>('');

  // Ports par défaut selon le framework
  const defaultPortsByFramework: Record<string, number> = {
    'python-django': 8000,
    'python-fastapi': 8000,
    'python-flask': 5000,
    'nodejs-express': 3000,
    'nodejs-nestjs': 3000,
    'react': 3000,
    'nextjs': 3000,
    'vue': 3000,
    'java-springboot': 8080,
    'go-gin': 8080,
    'php-laravel': 8000,
    // 8080 et pas 5000 : 5000 était le défaut d'ASP.NET Core AVANT .NET 8, et
    // l'API injecte ASPNETCORE_URLS sur le port retenu. Pré-remplir 5000 faisait
    // viser 5000 au Service pendant que le conteneur écoutait ailleurs — Service
    // sans endpoint, sondes en échec, Ingress 503.
    'dotnet': 8080,
    // SSR Node : un vrai serveur, pas des fichiers statiques (cf. registre API).
    'nuxt': 3000,
    'sveltekit': 3000,
    'remix': 3000,
    'ruby-rails': 3000,
    'html-static': 8080,  // nginx-unprivileged (imposé par le chart)
  };

  // États pour la sélection d'images buildées (CODE apps)
  const [builtImages, setBuiltImages] = useState<BuiltImage[]>([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [imageSourceMode, setImageSourceMode] = useState<'builtin' | 'manual'>('builtin');
  const [selectedBuiltImageId, setSelectedBuiltImageId] = useState<string | null>(null);

  const { register, handleSubmit, watch, setValue, control, formState: { errors } } = useForm<DeploymentForm>();
  const { fields: additionalDbFields, append: appendAdditionalDb, remove: removeAdditionalDb } = useFieldArray({
    control,
    name: 'additional_databases',
  });
  const watchProjectId = watch('project_id');

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (watchProjectId) {
      loadEnvironments(watchProjectId);
    }
  }, [watchProjectId]);

  // Effet pour pré-sélectionner l'environnement si fourni dans l'URL
  useEffect(() => {
    if (environmentIdFromUrl && environments.length > 0) {
      const selectedEnvironment = environments.find(env => env.id === environmentIdFromUrl);
      if (selectedEnvironment) {
        setValue('environment_id', environmentIdFromUrl);
        setValue('project_id', selectedEnvironment.projet_id);
        // Si environnement pré-sélectionné, commencer à l'étape du type d'app (1)
        // L'étape environnement (2) sera automatiquement pré-remplie
      }
    }
  }, [environmentIdFromUrl, environments, setValue]);

  // Auto-select first project when projects load
  useEffect(() => {
    if (projects.length > 0 && !watch('project_id')) {
      setValue('project_id', projects[0].id);
      loadEnvironments(projects[0].id);
    }
  }, [projects]);

  // Auto-select first environment when environments load
  useEffect(() => {
    if (environments.length > 0 && !watch('environment_id')) {
      setValue('environment_id', environments[0].id);
    }
  }, [environments]);

  const loadData = async () => {
    try {
      setLoading(true);
      const projectsData = await apiService.getProjects();
      setProjects(projectsData);
      
      // Si on a un environmentId dans l'URL, charger tous les environnements
      if (environmentIdFromUrl) {
        const allEnvironments: Environment[] = [];
        for (const project of projectsData) {
          try {
            const envs = await apiService.getProjectEnvironments(project.id);
            allEnvironments.push(...envs);
          } catch (error) {
            Logger.error('Failed to load environments for project');
          }
        }
        setEnvironments(allEnvironments);
      }
    } catch (error) {
      Logger.error('Failed to load deployment data', error);
      showErrorToast(error, ErrorMessages.PROJECT_LOAD_FAILED);
    } finally {
      setLoading(false);
    }
  };

  const loadEnvironments = async (projectId: string) => {
    try {
      const environmentsData = await apiService.getProjectEnvironments(projectId);
      setEnvironments(environmentsData);
    } catch (error) {
      Logger.error('Failed to load environments', error);
      showErrorToast(error, t('deploy.loadEnvError') as string);
    }
  };

  // Charger les images buildées pour les applications CODE (filtrées par projet)
  const loadBuiltImages = async () => {
    try {
      setLoadingImages(true);
      const response = await apiService.getBuiltImages({
        successful_only: true,
        limit: 50,
        project_id: watchProjectId || undefined,
      });
      setBuiltImages(response.builds || []);
    } catch (error) {
      Logger.error('Failed to load built images');
      setBuiltImages([]);
    } finally {
      setLoadingImages(false);
    }
  };

  // Charger les images buildées quand l'utilisateur sélectionne une app CODE ou change de projet
  useEffect(() => {
    if (appType === 'code' && watchProjectId) {
      loadBuiltImages();
    }
  }, [appType, watchProjectId]);

  // Keycloak : charger les PostgreSQL candidats de l'environnement choisi.
  //
  // On attend l'environnement (étape 2) : les candidats sont ceux du MÊME
  // namespace, puisque le pod Keycloak joint son PostgreSQL par un nom de
  // Service, qui ne résout que là.
  const watchEnvironmentId = watch('environment_id');
  useEffect(() => {
    // Toutes les applications managées peuvent se brancher sur un PostgreSQL
    // existant, pas seulement Keycloak. Cette condition, restée sur 'keycloak',
    // vidait la liste pour n8n et Metabase : la console annonçait « aucun
    // PostgreSQL managé » alors que l'API en proposait un, et le client ne
    // pouvait pas faire par l'écran ce que l'API autorisait.
    if (!APPS_VIA_ENDPOINT_MANAGE.includes(selectedTemplate?.type ?? '') || !watchEnvironmentId) {
      setKeycloakDatabases([]);
      return;
    }
    let cancelled = false;
    setLoadingKeycloakDatabases(true);
    setKeycloakDatabasesError(false);
    apiService
      .getKeycloakEligibleDatabases(watchEnvironmentId)
      .then((res) => {
        if (!cancelled) setKeycloakDatabases(res.databases);
      })
      .catch((error) => {
        Logger.error('Failed to load Keycloak eligible databases', error);
        if (!cancelled) {
          setKeycloakDatabases([]);
          setKeycloakDatabasesError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingKeycloakDatabases(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedTemplate?.type, watchEnvironmentId]);

  // Quand une image buildée est sélectionnée, mettre à jour les champs du formulaire
  const handleBuiltImageSelect = (buildId: string) => {
    setSelectedBuiltImageId(buildId);
    const selectedImage = builtImages.find(img => img.build_id === buildId);
    if (selectedImage) {
      // Extraire l'URL sans le tag si l'URL contient déjà le tag
      let imageUrl = selectedImage.image_url;
      const imageTag = selectedImage.image_tag || 'latest';

      // Si l'URL contient déjà le tag, le retirer pour éviter la duplication
      if (imageUrl.includes(':') && imageUrl.endsWith(`:${imageTag}`)) {
        imageUrl = imageUrl.replace(`:${imageTag}`, '');
      } else if (imageUrl.includes(':')) {
        // L'URL contient un tag différent, extraire juste le repository
        const lastColonIndex = imageUrl.lastIndexOf(':');
        const potentialTag = imageUrl.substring(lastColonIndex + 1);
        // Vérifier que ce n'est pas un port (ne contient que des chiffres ou des caractères de tag valides)
        if (!/^\d+$/.test(potentialTag)) {
          imageUrl = imageUrl.substring(0, lastColonIndex);
        }
      }

      setValue('docker_image_url', imageUrl);
      setValue('docker_image_tag', imageTag);
    }
  };

  // Sélecteur d'image NubiBuild pour le job de migration (même logique que handleBuiltImageSelect)
  const handleMigrationBuiltImageSelect = (buildId: string) => {
    setMigrationSelectedBuiltImageId(buildId);
    const selectedImage = builtImages.find(img => img.build_id === buildId);
    if (selectedImage) {
      let imageUrl = selectedImage.image_url;
      const imageTag = selectedImage.image_tag || 'latest';

      if (imageUrl.includes(':') && imageUrl.endsWith(`:${imageTag}`)) {
        imageUrl = imageUrl.replace(`:${imageTag}`, '');
      } else if (imageUrl.includes(':')) {
        const lastColonIndex = imageUrl.lastIndexOf(':');
        const potentialTag = imageUrl.substring(lastColonIndex + 1);
        if (!/^\d+$/.test(potentialTag)) {
          imageUrl = imageUrl.substring(0, lastColonIndex);
        }
      }

      setMigrationImageUrl(imageUrl);
      setMigrationImageTag(imageTag);
    }
  };

  // Mapper les types de template frontend vers les types backend
  const mapTemplateTypeToBackend = (templateType: string): string => {
    // Framework-based mapping for CODE applications
    const typeMapping: Record<string, string> = {
      // Frontend frameworks → "web"
      'react': 'web',
      'nextjs': 'web',
      'vue': 'web',
      'html-static': 'web',
      // Python frameworks → "python"
      'python-django': 'python',
      'python-fastapi': 'python',
      'python-flask': 'python',
      // Node.js frameworks → "node"
      'nodejs-express': 'node',
      'nodejs-nestjs': 'node',
      // Other frameworks
      'java-springboot': 'web',
      'go-gin': 'web',
      'php-laravel': 'web',
      'dotnet': 'web',
      'ruby-rails': 'web',
      // SSR Node : ils rendent le HTML depuis un serveur, donc `web` comme un
      // backend — pas `frontend`, qui signifie « statiques servis par nginx ».
      'nuxt': 'web',
      'sveltekit': 'web',
      'remix': 'web',
      // SaaS applications (ERP)
      'odoo': 'odoo',
      'wordpress': 'wordpress'
    };

    return typeMapping[templateType] || 'web';
  };

  const getSaaSConfiguration = (type: string): SaaSConfiguration => {
    const configs: Record<string, SaaSConfiguration> = {
      odoo: {
        cpu: 1.0, // 1 core
        ram: 2.0, // 2 GB
        image: 'odoo',
        tag: '16.0',
        helm_version: '1.0.0',
        helm_values: {
          service: {
            type: 'ClusterIP',
            port: 8069
          }
        },
        database_config: {
          engine: 'postgresql',
          database: 'odoo',
          user: 'odoo'
        }
      },
      wordpress: {
        cpu: 0.5, // 0.5 cores  
        ram: 0.5, // 0.5 GB
        image: 'wordpress',
        tag: '6.4-apache',
        helm_version: '1.0.0',
        helm_values: {
          service: {
            type: 'ClusterIP',
            port: 80
          }
        },
        database_config: {
          engine: 'mysql',
          database: 'wordpress',
          user: 'wordpress'
        }
      },
      joomla: {
        cpu: 0.5, // 0.5 cores
        ram: 0.5, // 0.5 GB
        image: 'joomla',
        tag: '4.4-apache',
        helm_version: '1.0.0',
        helm_values: {
          service: {
            type: 'ClusterIP',
            port: 80
          }
        },
        database_config: {
          engine: 'mysql',
          database: 'joomla',
          user: 'joomla'
        }
      },
      drupal: {
        cpu: 0.5, // 0.5 cores
        ram: 0.5, // 0.5 GB
        image: 'drupal',
        tag: '10.1-apache',
        helm_version: '1.0.0',
        helm_values: {
          service: {
            type: 'ClusterIP',
            port: 80
          }
        },
        database_config: {
          engine: 'mysql',
          database: 'drupal',
          user: 'drupal'
        }
      },
      nextcloud: {
        cpu: 0.5, // 0.5 cores
        ram: 1.0, // 1 GB
        image: 'nextcloud',
        tag: '28.0-apache',
        helm_version: '1.0.0',
        helm_values: {
          service: {
            type: 'ClusterIP',
            port: 80
          }
        },
        database_config: {
          engine: 'mysql',
          database: 'nextcloud',
          user: 'nextcloud'
        }
      },
      matomo: {
        cpu: 300,
        ram: 512,
        image: 'matomo',
        tag: '4.15-apache',
        helm_version: '1.0.0',
        helm_values: {
          service: {
            type: 'ClusterIP',
            port: 80
          }
        },
        database_config: {
          engine: 'mysql',
          database: 'matomo',
          user: 'matomo'
        }
      }
    };

    return configs[type] || configs.wordpress; // fallback to wordpress
  };

  const handleDeployClick = async () => {
    setShowConfirmation(true);
  };

  // Keycloak est presente parmi les APPLICATIONS (c'est ce que c'est), mais se
  // deploie par l'endpoint des services manages. Le chemin interne ne regarde
  // pas l'utilisateur : ce booleen est le seul endroit ou les deux se croisent.
  // Applications managees reellement deployables. Liste unique : elle sert a la
  // fois a griser les cartes et a les ORDONNER, les deux ne pouvant pas diverger.
  // Keycloak en tete — c'est la brique disponible la plus recente et la plus
  // demandee ; un utilisateur qui ouvre la categorie doit la voir sans chercher.
  const MANAGED_APPS_DISPONIBLES = ['keycloak', 'n8n', 'metabase', 'hasura', 'docuseal', 'openwebui', 'odoo', 'wordpress'];

  // Applications managees : presentees parmi les APPLICATIONS, deployees par
  // l'endpoint des services manages. Le chemin interne ne regarde pas
  // l'utilisateur ; ce booleen est le seul endroit ou les deux se croisent.
  const APPS_VIA_ENDPOINT_MANAGE = ['keycloak', 'n8n', 'metabase', 'hasura', 'docuseal', 'openwebui'];
  const usesManagedEndpoint =
    appType === 'managed' || APPS_VIA_ENDPOINT_MANAGE.includes(selectedTemplate?.type ?? '');

  const confirmDeploy = async () => {
    const data = watch(); // Get current form data
    if (!data.environment_id || !selectedTemplate) {
      showErrorToast(null, ErrorMessages.VALIDATION_ERROR);
      return;
    }

    try {
      setDeploying(true);
      setShowConfirmation(false);
      
      if (appType === 'code') {
        // Validate that Docker image URL is provided
        if (!data.docker_image_url || !data.docker_image_url.trim()) {
          showErrorToast(null, t('deploy.dockerImageUrlRequiredError') as string);
          setDeploying(false);
          return;
        }

        /* UN WORKER SANS COMMANDE EST LE BUG QU'ON CORRIGE. Sans elle, le pod lance
           l'ENTRYPOINT de l'image -- c'est-a-dire le serveur web -- et on obtient une
           deuxieme copie de l'API au lieu d'un worker. C'est litteralement l'incident qui a
           motive ce lot. Le chart ne peut pas le refuser (un `fail` au rendu bloquerait tous
           les syncs ArgoCD de l'application), donc la saisie est le seul filet. */
        if (workloadType !== 'web' && !startCommand.trim()) {
          showErrorToast(null, t('deploy.startCommandRequiredError') as string);
          setDeploying(false);
          return;
        }
        /* UN CRON SANS PLANIFICATION FAIT ECHOUER LE RENDU HELM, pas seulement ce Job :
           `cronjob.yaml` porte un `required`, donc ArgoCD passe en ComparisonError et
           n'applique plus RIEN sur cette application. */
        if (workloadType === 'cron' && !cronSchedule.trim()) {
          showErrorToast(null, t('deploy.cronScheduleRequiredError') as string);
          setDeploying(false);
          return;
        }

        // Convert environment variables array to object format (non-sensibles)
        const envVars: Record<string, string> = {};
        environmentVariables.forEach(envVar => {
          if (envVar.name && envVar.value) {
            envVars[envVar.name] = envVar.value;
          }
        });

        // Convert sensitive variables array to object format (secrets)
        const secrets: Record<string, string> = {};
        sensitiveVariables.forEach(secretVar => {
          if (secretVar.name && secretVar.value) {
            secrets[secretVar.name] = secretVar.value;
          }
        });

        // Deploy Code Application with pre-built image
        const deploymentPayload = {
          nom: data.app_name,
          cpu: parseFloat(data.cpu) || 0.5,
          ram: parseFloat(data.ram) || 1.0,
          replicas: parseInt(data.replicas) || 1,
          type_application: mapTemplateTypeToBackend(selectedTemplate.type),  // "web", "python", or "node"
          framework: selectedTemplate.type,  // REQUIS: framework ID (python-django, nodejs-express, etc.)
          image_registry_url: data.docker_image_url.trim(),
          image_tag: data.docker_image_tag?.trim() || 'latest',
          environment_variables: envVars,
          sensitive_variables: secrets,
          /* UN WORKLOAD SANS SERVICE NE DOIT PAS DEMANDER D'INGRESS. Le chart ne conditionne
             `ingress.yaml` que sur `exposePublic` : un worker expose produirait un Ingress dont
             le Service backend n'existe pas. L'API le force aussi (seul point de passage commun
             a la creation et a l'update) -- ici c'est pour que l'ECRAN et l'envoi disent la
             meme chose que ce qui sera applique. */
          expose_public: workloadType === 'web' ? exposePublic : false,
          port: appPort,  // Port sur lequel l'application écoute
          // Type de charge : worker (Deployment seul) / cron (CronJob) / web (défaut)
          // `undefined` pour `web` A LA CREATION : rien n'est emis, donc le values.yaml d'une
          // app web neuve reste identique a ce qu'il etait. C'est au PUT que la console envoie
          // la valeur explicite, sans quoi le retour arriere ne pourrait pas atterrir.
          workload_type: workloadType !== 'web' ? workloadType : undefined,
          schedule: workloadType === 'cron' ? cronSchedule.trim() : undefined,
          // Decoupee cote client (`parseCommand`, deja en service pour la commande de
          // migration) et envoyee en LISTE : forme exec, donc PID 1, donc SIGTERM recu.
          command: startCommand.trim() ? parseCommand(startCommand) : undefined,
          health_check_path: workloadType === 'web' ? healthCheckPath : undefined,
          // nginx.conf custom (chart >= 0.5.0). Envoyé même vide pour reset au CREATE.
          nginx_custom_config: nginxCustomConfig || null,
          migration_enabled: migrationEnabled,
          migration_command: migrationEnabled && migrationCommand.trim() ? parseCommand(migrationCommand) : null,
          migration_block_on_failure: migrationBlockOnFailure,
          // Image dediee a la migration (null = utilise l'image principale via fallback chart)
          migration_image_repository: migrationEnabled && migrationUseCustomImage ? migrationImageUrl : null,
          migration_image_tag: migrationEnabled && migrationUseCustomImage ? migrationImageTag : null,
          helm_chart_version: '1.0.0',
          /* MASQUER UN CHAMP NE LE VIDE PAS. Si l'utilisateur saisit un domaine en `web`
             puis bascule en `worker`, `register('custom_domain')` garde la valeur et elle
             partirait quand meme. */
          nom_domaine: workloadType === 'web' ? (data.custom_domain?.trim() || null) : null
        };

        await apiService.deployCodeApplication(data.environment_id, deploymentPayload);
      } else if (usesManagedEndpoint) {
        // Deploy Managed Service (Database or Broker)
        const defaultConfig = selectedTemplate.defaultConfig ?? { cpu: 0.5, ram: 1.0, storage: 10, version: 'latest' };
        const managedPayload = {
          nom: data.app_name,
          cpu: parseFloat(data.cpu) || defaultConfig.cpu,
          ram: parseFloat(data.ram) || defaultConfig.ram,
          storage: parseFloat(data.storage) || defaultConfig.storage,
          replicas: parseInt(data.replicas) || 1,
          type_application: selectedTemplate.type,
          // La version des APPLICATIONS MANAGÉES n'est PAS envoyée : elle vient
          // du registre serveur (`managed_apps`), qui la valide et n'en accepte
          // qu'une. La recopier ici créait un doublon qui a divergé sur TROIS
          // des six applications — Keycloak, n8n et Metabase étaient devenues
          // indéployables, avec « Version non supportée » au visage du client.
          // `version` est facultatif côté API : absent, le défaut du registre
          // s'applique.
          //
          // Les bases et brokers gardent leur version : là c'est un vrai choix
          // (PostgreSQL 15 ou 16), et le registre en accepte plusieurs.
          ...(APPS_VIA_ENDPOINT_MANAGE.includes(selectedTemplate.type)
            ? {}
            : { version: defaultConfig.version }),
          enable_backups: data.enable_backups || false,
          // Keycloak n'a ni mot de passe root, ni base/utilisateur a saisir :
          // son admin est genere par la plateforme et sa base est provisionnee
          // sur le PostgreSQL hote. Envoyer les champs des autres services
          // ferait echouer la validation du schema.
          ...(APPS_VIA_ENDPOINT_MANAGE.includes(selectedTemplate.type)
            ? {
                ...(data.database_deployment_id ? { database_deployment_id: data.database_deployment_id } : {}),
                ...(data.custom_domain?.trim() ? { custom_domain: data.custom_domain.trim() } : {}),
              }
            : {
                root_password: data.root_password!, // Required by form validation
                database: data.database || 'app_db',
                username: data.username || 'app_user',
                password: data.password || undefined,
              }),
          // PostgreSQL uniquement: bases additionnelles (filtre les rows vides)
          ...(selectedTemplate.type === 'postgresql' && data.additional_databases?.length
            ? {
                additional_databases: data.additional_databases
                  .filter((d) => d?.name?.trim())
                  .map((d) => ({
                    name: d.name.trim(),
                    ...(d.user?.trim() ? { user: d.user.trim() } : {}),
                    ...(d.password ? { password: d.password } : {}),
                  })),
              }
            : {}),
          // PostgreSQL uniquement: extensions (postgis, pgvector)
          ...(selectedTemplate.type === 'postgresql' && pgExtensions.length
            ? { extensions: pgExtensions }
            : {}),
        };

        const created = await apiService.deployManagedService(data.environment_id, managedPayload);
        // Keycloak : le mot de passe administrateur n'est renvoye QU'ICI. Aucune
        // lecture ulterieure ne le reexpose — on interrompt donc la redirection
        // automatique tant que l'utilisateur ne l'a pas note.
        // Meme regle pour la base dediee : l'ecran de creation est la seule
        // occasion d'en lire le mot de passe.
        if (created?.admin_credentials || created?.database_credentials) {
          setKeycloakCredentials(created.admin_credentials ?? null);
          setDatabaseCredentials(created.database_credentials ?? null);
          setDeploymentSuccess(true);
          showSuccessToast(SuccessMessages.DEPLOYMENT_CREATED);
          setDeploying(false);
          return;
        }
      } else {
        // Deploy ERP/SaaS Application
        const appConfig = getSaaSConfiguration(selectedTemplate.type);
        const erpPayload = {
          nom: data.app_name,
          cpu: parseFloat(data.cpu) || appConfig.cpu, // Use form value or template default
          ram: parseFloat(data.ram) || appConfig.ram, // Use form value or template default
          storage: parseFloat(data.storage) || 20, // Database storage in GB
          replicas: parseInt(data.replicas) || 1, // Use form value or default
          type_application: mapTemplateTypeToBackend(selectedTemplate.type),
          image_registry_url: appConfig.image, // Use image_registry_url instead of url_image
          image_tag: selectedTemplate.type === 'odoo' && data.image_tag ? data.image_tag : appConfig.tag,
          // helm_chart_version: Automatiquement défini à 0.1.0 par le backend
          helm_values: appConfig.helm_values,
          database_config: appConfig.database_config,
          // Configuration spécifique pour Odoo
          ...(selectedTemplate.type === 'odoo' && {
            odoo_email: data.odoo_email || 'admin@example.com',
            odoo_password: data.odoo_password,
            postgres_password: data.postgres_password,
            odoo_language: data.odoo_language || 'fr_FR'
          }),
          nom_domaine: data.custom_domain?.trim() || null,
          // Configuration spécifique pour WordPress
          ...(selectedTemplate.type === 'wordpress' && {
            wordpress_email: data.wordpress_email || 'admin@nubitech.io',
            wordpress_password: data.wordpress_password,
            mariadb_root_password: data.mariadb_root_password,
            mariadb_user_password: data.mariadb_user_password
          })
        };

        // Log deployment info without sensitive data (passwords)
        const sanitizedERPPayload = {
          ...erpPayload,
          // Hide all password fields
          odoo_password: erpPayload.odoo_password ? '***HIDDEN***' : undefined,
          postgres_password: erpPayload.postgres_password ? '***HIDDEN***' : undefined,
          wordpress_password: erpPayload.wordpress_password ? '***HIDDEN***' : undefined,
          mariadb_root_password: erpPayload.mariadb_root_password ? '***HIDDEN***' : undefined,
          mariadb_user_password: erpPayload.mariadb_user_password ? '***HIDDEN***' : undefined
        };
        // Deployment payload prepared for ERP/SaaS application
        
        await apiService.deployERPApplication(data.environment_id, erpPayload);
      }

      // Marquer le deploiement comme reussi
      setDeploymentSuccess(true);
      showSuccessToast(SuccessMessages.DEPLOYMENT_CREATED);

      // Attendre un peu pour que l'utilisateur voie le message de succès
      setTimeout(() => {
        // Rediriger vers la page des applications déployées
        if (data.environment_id) {
          navigate(`/deployments/${data.environment_id}`);
        } else {
          navigate('/deployments');
        }
      }, 2000);
      
    } catch (error: unknown) {
      Logger.error('Deployment failed', error);

      // Check if it's a quota error
      const apiError = error as { response?: { status?: number; data?: { detail?: QuotaErrorInfo } } };
      if (apiError.response?.status === 400 && apiError.response?.data?.detail &&
          typeof apiError.response.data.detail === 'object' &&
          apiError.response.data.detail.message &&
          apiError.response.data.detail.quota_exceeded) {
        // It's a quota error, show the quota modal
        setQuotaError(apiError.response.data.detail);
        setShowQuotaError(true);
      } else {
        // Handle other errors with centralized toast utility
        showErrorToast(error, ErrorMessages.DEPLOYMENT_CREATE_FAILED);
      }

      setDeploying(false);
    }
  };

  const onSubmit = async (data: DeploymentForm) => {
    // This function is now just for form validation, actual deployment is handled by confirmDeploy
    return;
  };

  const codeTemplates = [
    {
      title: 'Django',
      subtitle: 'Python',
      description: t('templates.django') as string,
      color: 'bg-emerald-500',
      textColor: 'text-emerald-500',
      borderColor: 'border-emerald-500',
      type: 'python-django',
      icon: <BrandLogo type="python-django" />
    },
    {
      title: 'FastAPI',
      subtitle: 'Python',
      description: t('templates.fastapi') as string,
      color: 'bg-emerald-600',
      textColor: 'text-emerald-600',
      borderColor: 'border-emerald-600',
      type: 'python-fastapi',
      icon: <BrandLogo type="python-fastapi" />
    },
    {
      title: 'Flask',
      subtitle: 'Python',
      description: t('templates.flask') as string,
      color: 'bg-emerald-700',
      textColor: 'text-emerald-700',
      borderColor: 'border-emerald-700',
      type: 'python-flask',
      icon: <BrandLogo type="python-flask" />
    },
    {
      title: 'Express',
      subtitle: 'Node.js',
      description: t('templates.express') as string,
      color: 'bg-green-500',
      textColor: 'text-green-500',
      borderColor: 'border-green-500',
      type: 'nodejs-express',
      icon: <BrandLogo type="nodejs-express" />
    },
    {
      title: 'NestJS',
      subtitle: 'Node.js',
      description: t('templates.nestjs') as string,
      color: 'bg-green-600',
      textColor: 'text-green-600',
      borderColor: 'border-green-600',
      type: 'nodejs-nestjs',
      icon: <BrandLogo type="nodejs-nestjs" />
    },
    {
      title: 'React',
      subtitle: 'Frontend',
      description: t('templates.react') as string,
      color: 'bg-accent-500',
      textColor: 'text-accent-500',
      borderColor: 'border-accent-500',
      type: 'react',
      icon: <BrandLogo type="react" />
    },
    {
      title: 'Next.js',
      subtitle: 'Frontend',
      description: t('templates.nextjs') as string,
      color: 'bg-accent-600',
      textColor: 'text-accent-600',
      borderColor: 'border-accent-600',
      type: 'nextjs',
      icon: <BrandLogo type="nextjs" />
    },
    {
      title: 'Vue.js',
      subtitle: 'Frontend',
      description: t('templates.vue') as string,
      color: 'bg-accent-700',
      textColor: 'text-accent-700',
      borderColor: 'border-accent-700',
      type: 'vue',
      icon: <BrandLogo type="vue" />
    },
    {
      title: 'Spring Boot',
      subtitle: 'Java',
      description: t('templates.springboot') as string,
      color: 'bg-orange-500',
      textColor: 'text-orange-500',
      borderColor: 'border-orange-500',
      type: 'java-springboot',
      icon: <BrandLogo type="java-springboot" />
    },
    {
      title: 'Gin',
      subtitle: 'Go',
      description: t('templates.gin') as string,
      color: 'bg-cyan-500',
      textColor: 'text-cyan-500',
      borderColor: 'border-cyan-500',
      type: 'go-gin',
      icon: <BrandLogo type="go-gin" />
    },
    {
      title: 'Laravel',
      subtitle: 'PHP',
      // La mention du Dockerfile n'est pas cosmétique : le buildpack PHP de
      // Paketo exige le stack `full`, que le ClusterBuilder n'utilise pas. Un
      // Laravel sans Dockerfile ne trouve donc aucun buildpack et le build
      // échoue — autant le dire ici plutôt qu'après.
      description: `${t('templates.laravel')}\n${t('templates.phpLaravelNote')}`,
      color: 'bg-red-500',
      textColor: 'text-red-500',
      borderColor: 'border-red-500',
      type: 'php-laravel',
      icon: <BrandLogo type="php-laravel" />
    },
    {
      title: '.NET',
      subtitle: 'C#',
      description: t('templates.dotnet') as string,
      color: 'bg-purple-500',
      textColor: 'text-purple-500',
      borderColor: 'border-purple-500',
      type: 'dotnet',
      icon: <BrandLogo type="dotnet" />
    },
    {
      title: 'Ruby on Rails',
      subtitle: 'Ruby',
      description: t('templates.rubyRails') as string,
      color: 'bg-red-600',
      textColor: 'text-red-600',
      borderColor: 'border-red-600',
      type: 'ruby-rails',
      icon: <BrandLogo type="ruby-rails" />
    },
    {
      title: 'Nuxt',
      subtitle: 'Vue SSR',
      description: t('templates.nuxt') as string,
      color: 'bg-emerald-500',
      textColor: 'text-emerald-500',
      borderColor: 'border-emerald-500',
      type: 'nuxt',
      icon: <BrandLogo type="nuxt" />
    },
    {
      title: 'SvelteKit',
      subtitle: 'Svelte SSR',
      description: t('templates.sveltekit') as string,
      color: 'bg-orange-600',
      textColor: 'text-orange-600',
      borderColor: 'border-orange-600',
      type: 'sveltekit',
      icon: <BrandLogo type="sveltekit" />
    },
    {
      title: 'Remix',
      subtitle: 'React SSR',
      description: t('templates.remix') as string,
      color: 'bg-slate-600',
      textColor: 'text-slate-600',
      borderColor: 'border-slate-600',
      type: 'remix',
      icon: <BrandLogo type="remix" />
    },
    {
      title: 'HTML / CSS',
      subtitle: t('templates.htmlStaticSubtitle') as string,
      description: t('templates.htmlStatic') as string,
      color: 'bg-slate-500',
      textColor: 'text-slate-500',
      borderColor: 'border-slate-500',
      type: 'html-static',
      icon: <BrandLogo type="html-static" />
    }
  ];

  const saasTemplates = [
    {
      title: 'Odoo',
      subtitle: t('templates.odooSubtitle') as string,
      description: t('templates.odoo') as string,
      color: 'bg-purple-600',
      textColor: 'text-purple-600',
      borderColor: 'border-purple-600',
      type: 'odoo',
      icon: <BrandLogo type="odoo" />
    },
    {
      title: 'WordPress',
      subtitle: t('templates.wordpressSubtitle') as string,
      description: t('templates.wordpress') as string,
      color: 'bg-accent-600', 
      textColor: 'text-accent-600',
      borderColor: 'border-accent-600',
      type: 'wordpress',
      icon: <BrandLogo type="wordpress" />
    },
    {
      title: 'Joomla',
      subtitle: t('templates.joomlaSubtitle') as string,
      description: t('templates.joomla') as string,
      color: 'bg-orange-500',
      textColor: 'text-orange-500',
      borderColor: 'border-orange-500',
      type: 'joomla',
      icon: <BrandLogo type="joomla" />
    },
    {
      title: 'Drupal',
      subtitle: t('templates.drupalSubtitle') as string,
      description: t('templates.drupal') as string,
      color: 'bg-accent-800',
      textColor: 'text-accent-800',
      borderColor: 'border-accent-800',
      type: 'drupal',
      icon: <BrandLogo type="drupal" />
    },
    {
      title: 'Nextcloud',
      subtitle: t('templates.nextcloudSubtitle') as string,
      description: t('templates.nextcloud') as string,
      color: 'bg-accent-500',
      textColor: 'text-accent-500',
      borderColor: 'border-accent-500',
      type: 'nextcloud',
      icon: <BrandLogo type="nextcloud" />
    },
    {
      title: 'Matomo',
      subtitle: t('templates.matomoSubtitle') as string,
      description: t('templates.matomo') as string,
      color: 'bg-orange-600',
      textColor: 'text-orange-600',
      borderColor: 'border-orange-600',
      type: 'matomo',
      icon: <BrandLogo type="matomo" />
    },
    {
      title: 'Keycloak',
      subtitle: t('templates.keycloakSubtitle') as string,
      description: t('templates.keycloak') as string,
      color: 'bg-sky-600',
      textColor: 'text-sky-600',
      borderColor: 'border-sky-600',
      type: 'keycloak',
      icon: <BrandLogo type="keycloak" />,
      defaultConfig: {
        cpu: 0.5,
        ram: 1.5,
        // Keycloak ne monte aucun volume : son état vit dans le PostgreSQL
        // hôte. Lui demander du stockage gonflerait le quota pour rien.
        storage: 0,
      }
    },
    {
      title: 'n8n',
      subtitle: t('templates.n8nSubtitle') as string,
      description: t('templates.n8n') as string,
      color: 'bg-rose-500',
      textColor: 'text-rose-500',
      borderColor: 'border-rose-500',
      type: 'n8n',
      icon: <BrandLogo type="n8n" />,
      defaultConfig: {
        cpu: 0.5,
        ram: 1.0,
        // Aucun volume : workflows et identifiants vivent dans le PostgreSQL.
        storage: 0,
      }
    },
    {
      title: 'Metabase',
      subtitle: t('templates.metabaseSubtitle') as string,
      description: t('templates.metabase') as string,
      color: 'bg-blue-500',
      textColor: 'text-blue-500',
      borderColor: 'border-blue-500',
      type: 'metabase',
      icon: <BrandLogo type="metabase" />,
      defaultConfig: {
        cpu: 0.5,
        // Metabase est une JVM : en dessous de 2 Go elle passe son temps en GC.
        ram: 2.0,
        storage: 0,
      }
    },
    {
      title: 'Hasura',
      subtitle: t('templates.hasuraSubtitle') as string,
      description: t('templates.hasura') as string,
      color: 'bg-indigo-500',
      textColor: 'text-indigo-500',
      borderColor: 'border-indigo-500',
      type: 'hasura',
      icon: <BrandLogo type="hasura" />,
      defaultConfig: {
        cpu: 0.5,
        ram: 1.0,
        // Aucun volume : les metadonnees vivent dans le PostgreSQL hote.
        storage: 0,
      }
    },
    {
      title: 'DocuSeal',
      subtitle: t('templates.docusealSubtitle') as string,
      description: t('templates.docuseal') as string,
      color: 'bg-sky-600',
      textColor: 'text-sky-600',
      borderColor: 'border-sky-600',
      type: 'docuseal',
      icon: <BrandLogo type="docuseal" />,
      defaultConfig: {
        cpu: 0.5,
        // Rails plus une file de traitement dans le meme processus.
        ram: 2.0,
        // Aucun volume : les donnees vont dans le PostgreSQL hote, les
        // documents dans un bucket NubiS3 provisionne avec l'application.
        storage: 0,
      }
    },
    {
      title: 'Open WebUI',
      subtitle: t('templates.openwebuiSubtitle') as string,
      description: t('templates.openwebui') as string,
      color: 'bg-violet-600',
      textColor: 'text-violet-600',
      borderColor: 'border-violet-600',
      type: 'openwebui',
      icon: <BrandLogo type="openwebui" />,
      defaultConfig: {
        cpu: 0.5,
        ram: 2.0,
        // Aucun volume : conversations et embeddings dans le PostgreSQL hote
        // (pgvector), fichiers televerses dans un bucket NubiS3.
        storage: 0,
      }
    }
  ];

  const managedServiceTemplates = [
    {
      title: 'PostgreSQL',
      subtitle: t('templates.postgresqlSubtitle') as string,
      description: t('templates.postgresql') as string,
      color: 'bg-accent-600',
      textColor: 'text-accent-600',
      borderColor: 'border-accent-600',
      type: 'postgresql',
      icon: <BrandLogo type="postgresql" />,
      defaultConfig: {
        cpu: 0.5,
        ram: 1.0,
        storage: 10,
        version: '15-alpine'
      }
    },
    {
      title: 'MySQL',
      subtitle: t('templates.mysqlSubtitle') as string,
      description: t('templates.mysql') as string,
      color: 'bg-orange-600',
      textColor: 'text-orange-600',
      borderColor: 'border-orange-600',
      type: 'mysql',
      icon: <BrandLogo type="mysql" />,
      defaultConfig: {
        cpu: 0.5,
        ram: 1.0,
        storage: 10,
        version: '8.0'
      }
    },
    // MongoDB retire du catalogue : le chart cote charts-factory est une
    // coquille vide (aucun template) et MongoDB Community est sous SSPL, ce qui
    // imposerait de publier toute la plateforme pour l'offrir en service manage.
    // L'API le rejette desormais en 422 — laisser la carte afficherait une
    // erreur au premier clic. Alternative permissive envisagee : FerretDB.
    {
      title: 'Redis',
      subtitle: t('templates.redisSubtitle') as string,
      description: t('templates.redis') as string,
      color: 'bg-red-600',
      textColor: 'text-red-600',
      borderColor: 'border-red-600',
      type: 'redis',
      icon: <BrandLogo type="redis" />,
      defaultConfig: {
        cpu: 0.25,
        ram: 0.5,
        storage: 2,
        version: '7-alpine'
      }
    },
    {
      title: 'RabbitMQ',
      subtitle: t('templates.rabbitmqSubtitle') as string,
      description: t('templates.rabbitmq') as string,
      color: 'bg-orange-500',
      textColor: 'text-orange-500',
      borderColor: 'border-orange-500',
      type: 'rabbitmq',
      icon: <BrandLogo type="rabbitmq" />,
      defaultConfig: {
        cpu: 0.5,
        ram: 1.0,
        storage: 8,
        version: '3.12-management-alpine'
      }
    },
    {
      title: 'Kafka',
      subtitle: t('templates.kafkaSubtitle') as string,
      description: t('templates.kafka') as string,
      color: 'bg-indigo-500',
      textColor: 'text-indigo-500',
      borderColor: 'border-indigo-500',
      type: 'kafka',
      icon: <BrandLogo type="kafka" />,
      defaultConfig: {
        // La version DOIT figurer dans KAFKA_METADATA_VERSIONS cote API
        // (app/services/managed_service_template_service.py), qui l'associe a
        // son metadataVersion. Une version absente est rejetee explicitement.
        cpu: 1.0,
        ram: 2.0,
        storage: 20,
        version: '4.3.1'
      }
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-500"></div>
      </div>
    );
  }

  return (
    <>
      {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-text-primary">{t('deploy.title')}</h1>
        </div>

        {/* Steps Navigation - 3 steps */}
        <div className="bg-surface-2 rounded-card shadow-card border border-border-1 p-6 mb-8">
          <div className="flex items-center justify-center mb-6">
            {/* Step 1 */}
            <div className="flex items-center">
              <div className={`flex items-center justify-center w-10 h-10 rounded-full ${step > 1 ? 'bg-accent-500 text-white' : step === 1 ? 'bg-accent-500 text-white' : 'bg-surface-3 text-text-secondary'}`}>
                {step > 1 ? <CheckIcon className="w-5 h-5" /> : '1'}
              </div>
              <div className={`w-24 h-1 mx-2 ${step > 1 ? 'bg-accent-500' : 'bg-surface-3'}`}></div>
            </div>
            {/* Step 2 */}
            <div className="flex items-center">
              <div className={`flex items-center justify-center w-10 h-10 rounded-full ${step > 2 ? 'bg-accent-500 text-white' : step === 2 ? 'bg-accent-500 text-white' : 'bg-surface-3 text-text-secondary'}`}>
                {step > 2 ? <CheckIcon className="w-5 h-5" /> : '2'}
              </div>
              <div className={`w-24 h-1 mx-2 ${step > 2 ? 'bg-accent-500' : 'bg-surface-3'}`}></div>
            </div>
            {/* Step 3 */}
            <div className="flex items-center">
              <div className={`flex items-center justify-center w-10 h-10 rounded-full ${step === 3 ? 'bg-accent-500 text-white' : 'bg-surface-3 text-text-secondary'}`}>
                3
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 text-center text-sm">
            <div>
              <div className={`font-medium ${step === 1 ? 'text-accent-500' : step > 1 ? 'text-text-primary' : 'text-text-secondary'}`}>{t('deploy.stepTypeTemplate')}</div>
              <div className="text-text-tertiary">{t('deploy.stepTechnology')}</div>
            </div>
            <div>
              <div className={`font-medium ${step === 2 ? 'text-accent-500' : step > 2 ? 'text-text-primary' : 'text-text-secondary'}`}>{t('deploy.stepEnvironment')}</div>
              <div className="text-text-tertiary">{t('deploy.stepWhereTodeploy')}</div>
            </div>
            <div>
              <div className={`font-medium ${step === 3 ? 'text-accent-500' : 'text-text-secondary'}`}>{t('deploy.stepConfigDeploy')}</div>
              <div className="text-text-tertiary">{t('deploy.stepParameters')}</div>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Step 1: Type & Template (merged) */}
          {step === 1 && (
            <div className="space-y-8">
              {templateSubStep === 'type' ? (
                <div>
                  <h2 className="text-2xl font-bold text-text-primary mb-6">{t('deploy.appType')}</h2>

                  {/* App Type Selection */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
                    <div
                      className={`bg-surface-2 rounded-card shadow-card border-2 ${appType === 'code' ? 'border-accent-500' : 'border-border-1'} p-8 cursor-pointer hover:shadow-md transition-shadow text-center`}
                      onClick={() => { setAppType('code'); setTemplateSubStep('template'); setSelectedTemplate(null); setTemplateFilter('all'); setTemplateSearch(''); }}
                    >
                      <div className="w-16 h-16 bg-accent-500 rounded-lg flex items-center justify-center mx-auto mb-4">
                        <BoltIcon className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-text-primary mb-3">{t('deploy.codeApps')}</h3>
                      <p className="text-text-secondary mb-4">
                        {t('deploy.codeAppsDescription')}
                      </p>
                      <ul className="text-sm text-text-tertiary space-y-1">
                        <li>{t('deploy.codeFeature1')}</li>
                        <li>{t('deploy.codeFeature2')}</li>
                        <li>{t('deploy.codeFeature3')}</li>
                      </ul>
                    </div>

                    <div
                      className={`bg-surface-2 rounded-card shadow-card border-2 ${appType === 'saas' ? 'border-green-500' : 'border-border-1'} p-8 cursor-pointer hover:shadow-md transition-shadow text-center`}
                      onClick={() => { setAppType('saas'); setTemplateSubStep('template'); setSelectedTemplate(null); }}
                    >
                      <div className="w-16 h-16 bg-green-500 rounded-lg flex items-center justify-center mx-auto mb-4">
                        <RocketLaunchIcon className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-text-primary mb-3">{t('deploy.saasApps')}</h3>
                      <p className="text-text-secondary mb-4">
                        {t('deploy.saasAppsDescription')}
                      </p>
                      <ul className="text-sm text-text-tertiary space-y-1">
                        <li>{t('deploy.saasFeature1')}</li>
                        <li>{t('deploy.saasFeature2')}</li>
                        <li>{t('deploy.saasFeature3')}</li>
                      </ul>
                    </div>

                    <div
                      className={`bg-surface-2 rounded-card shadow-card border-2 ${appType === 'managed' ? 'border-purple-500' : 'border-border-1'} p-8 cursor-pointer hover:shadow-md transition-shadow text-center`}
                      onClick={() => { setAppType('managed'); setTemplateSubStep('template'); setSelectedTemplate(null); }}
                    >
                      <div className="w-16 h-16 bg-purple-500 rounded-lg flex items-center justify-center mx-auto mb-4">
                        <ServerIcon className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-text-primary mb-3">{t('deploy.managedServices')}</h3>
                      <p className="text-text-secondary mb-4">
                        {t('deploy.managedServicesDescription')}
                      </p>
                      <ul className="text-sm text-text-tertiary space-y-1">
                        <li>{t('deploy.managedFeature1')}</li>
                        <li>{t('deploy.managedFeature2')}</li>
                        <li>{t('deploy.managedFeature3')}</li>
                      </ul>
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <button
                      type="button"
                      onClick={() => { setTemplateSubStep('type'); setSelectedTemplate(null); }}
                      className="text-text-tertiary hover:text-text-primary transition-colors"
                    >
                      {`← ${t('deploy.back')}`}
                    </button>
                    <h2 className="text-2xl font-bold text-text-primary">
                      {appType === 'code' ? t('deploy.codeTemplate') : appType === 'saas' ? t('deploy.saasTemplate') : t('deploy.managedTemplate')}
                    </h2>
                  </div>

                  {/* Filter pills + search for code apps */}
                  {appType === 'code' && (
                    <div className="flex flex-wrap items-center gap-3 mb-6">
                      <div className="flex gap-2">
                        {(['all', 'backend', 'frontend'] as const).map((filter) => (
                          <button
                            key={filter}
                            type="button"
                            onClick={() => setTemplateFilter(filter)}
                            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                              templateFilter === filter
                                ? 'bg-accent-500 text-white'
                                : 'bg-surface-3 text-text-secondary hover:bg-surface-4'
                            }`}
                          >
                            {filter === 'all' ? t('deploy.all') : filter === 'backend' ? t('deploy.backend') : t('deploy.frontend')}
                          </button>
                        ))}
                      </div>
                      <div className="relative flex-1 max-w-xs">
                        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-quaternary" />
                        <input
                          type="text"
                          value={templateSearch}
                          onChange={(e) => setTemplateSearch(e.target.value)}
                          placeholder={t('deploy.searchTemplate')}
                          className="w-full pl-9 pr-3 py-1.5 border border-border-1 bg-surface-3 text-text-primary rounded-full text-sm focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                        />
                      </div>
                    </div>
                  )}

                  {/* Templates based on selected app type */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {(() => {
                      const frontendTypes = ['react', 'nextjs', 'vue', 'html-static'];
                      const backendTypes = ['python-django', 'python-fastapi', 'python-flask', 'nodejs-express', 'nodejs-nestjs', 'java-springboot', 'go-gin', 'php-laravel', 'dotnet', 'ruby-rails', 'nuxt', 'sveltekit', 'remix'];
                      let templates = appType === 'code' ? codeTemplates : appType === 'managed' ? managedServiceTemplates : saasTemplates;

                      // Applications managees : les disponibles d'abord, dans
                      // l'ordre de MANAGED_APPS_DISPONIBLES, les autres ensuite.
                      // Sans ca Keycloak apparaissait apres quatre cartes
                      // grisees, et la seule brique nouvelle passait inapercue.
                      if (appType === 'saas') {
                        const rang = (t: string) => {
                          const i = MANAGED_APPS_DISPONIBLES.indexOf(t);
                          return i === -1 ? MANAGED_APPS_DISPONIBLES.length : i;
                        };
                        templates = [...templates].sort((a, b) => rang(a.type) - rang(b.type));
                      }

                      if (appType === 'code') {
                        if (templateFilter === 'frontend') {
                          templates = templates.filter(t => frontendTypes.includes(t.type));
                        } else if (templateFilter === 'backend') {
                          templates = templates.filter(t => backendTypes.includes(t.type));
                        }
                        if (templateSearch.trim()) {
                          const search = templateSearch.toLowerCase();
                          templates = templates.filter(t =>
                            t.title.toLowerCase().includes(search) ||
                            (t.subtitle && t.subtitle.toLowerCase().includes(search)) ||
                            t.type.toLowerCase().includes(search)
                          );
                        }
                      }

                      return templates.map((template, index) => {
                        const isDisabled = appType === 'saas' && !MANAGED_APPS_DISPONIBLES.includes(template.type);

                        return (
                          <div
                            key={index}
                            className={`rounded-card shadow-card border-2 p-6 transition-shadow ${
                              isDisabled
                                ? 'bg-surface-3 border-border-1 cursor-not-allowed opacity-50'
                                : `bg-surface-2 ${selectedTemplate?.type === template.type ? template.borderColor : 'border-border-1'} cursor-pointer hover:shadow-md`
                            }`}
                            onClick={() => {
                              if (!isDisabled) {
                                setSelectedTemplate(template as DeploymentTemplate);
                                setValue('template_type', template.type);
                                // Les defauts de la carte doivent ATTEINDRE le
                                // formulaire. Les champs portent un
                                // `defaultValue` fige a 0.5 / 1.0, et comme ils
                                // ont toujours une valeur, le `|| defaultConfig`
                                // de la soumission n'est jamais atteint : le
                                // `defaultConfig` de chaque modele etait donc
                                // decoratif. Metabase partait ainsi avec 1 Go au
                                // lieu des 2 que son registre demande -- une JVM
                                // qui passe son temps en GC.
                                const defauts = (template as DeploymentTemplate).defaultConfig;
                                if (defauts) {
                                  setValue('cpu', String(defauts.cpu));
                                  setValue('ram', String(defauts.ram));
                                }
                                const defaultPort = defaultPortsByFramework[template.type] || 8000;
                                setAppPort(defaultPort);
                              }
                            }}
                          >
                            <div className={`h-1 mb-4 rounded ${isDisabled ? 'bg-text-quaternary' : template.color}`}></div>
                            <div className="mb-4 text-center flex justify-center">
                              {template.icon}
                            </div>
                            <h3 className={`text-lg font-bold mb-2 ${isDisabled ? 'text-text-tertiary' : 'text-text-primary'}`}>
                              {template.title}
                            </h3>
                            <p className={`text-sm mb-4 ${isDisabled ? 'text-text-quaternary' : 'text-text-secondary'}`}>
                              {template.subtitle}
                            </p>
                            <p className={`text-sm whitespace-pre-line mb-4 ${isDisabled ? 'text-text-quaternary' : 'text-text-tertiary'}`}>
                              {template.description}
                            </p>

                            {isDisabled && (
                              <div className="mb-4 text-center">
                                <span className="inline-block bg-orange-100 text-orange-700 px-3 py-1 rounded-full font-bold text-sm border border-orange-300 shadow-sm">
                                  {t('deploy.comingSoon')}
                                </span>
                              </div>
                            )}

                            <button
                              type="button"
                              disabled={isDisabled}
                              className={`w-full px-4 py-2 rounded-md text-sm font-medium transition-opacity ${
                                isDisabled
                                  ? 'bg-surface-3 text-text-tertiary cursor-not-allowed'
                                  : selectedTemplate?.type === template.type
                                    ? template.color + ' text-white'
                                    : 'bg-surface-3 text-text-secondary hover:opacity-90'
                              }`}
                            >
                              {isDisabled ? t('deploy.notAvailable') : selectedTemplate?.type === template.type ? t('deploy.selected') : t('deploy.choose')}
                            </button>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Environment Selection */}
          {step === 2 && (
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-text-primary mb-6">{t('deploy.selectEnvironment')}</h2>

                {projects.length === 0 ? (
                  /* Message quand aucun projet n'existe */
                  <div className="bg-blue-500/12 border-2 border-blue-500/30 rounded-lg p-8 text-center">
                    <div className="flex justify-center mb-4">
                      <div className="flex items-center justify-center h-16 w-16 rounded-full bg-blue-500/12">
                        <svg className="h-8 w-8 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                        </svg>
                      </div>
                    </div>
                    <h3 className="text-xl font-semibold text-text-primary mb-2">
                      {t('deploy.noProjectsAvailable')}
                    </h3>
                    <p className="text-text-secondary mb-6 max-w-md mx-auto">
                      {t('deploy.noProjectsDescription')}
                    </p>
                    <Link
                      to="/projects"
                      className="inline-flex items-center px-6 py-3 bg-accent-600 hover:bg-accent-700 text-white font-medium rounded-lg transition-colors"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      {t('deploy.createProject')}
                    </Link>
                  </div>
                ) : (
                  /* Formulaire de sélection normal */
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Project Selection */}
                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-2">
                        {t('deploy.project')}
                      </label>
                      <select
                        {...register('project_id', { required: t('deploy.selectProjectRequired') as string })}
                        className="w-full px-3 py-2 border border-border-1 bg-surface-3 text-text-primary rounded-md focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                      >
                        <option value="">{t('deploy.selectProject')}</option>
                        {projects.map((project) => (
                          <option key={project.id} value={project.id}>
                            {project.nom}
                          </option>
                        ))}
                      </select>
                      {errors.project_id && (
                        <p className="mt-1 text-sm text-red-600">{errors.project_id.message}</p>
                      )}
                    </div>

                    {/* Environment Selection */}
                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-2">
                        {t('deploy.environmentLabel')}
                      </label>
                      {watchProjectId && environments.length === 0 ? (
                        /* Message quand aucun environnement pour le projet sélectionné */
                        <div className="bg-orange-500/12 border border-orange-500/30 rounded-lg p-4">
                          <p className="text-sm text-orange-300 mb-3">
                            {t('deploy.noEnvForProject')}
                          </p>
                          <Link
                            to={`/environments/${watchProjectId}`}
                            className="inline-flex items-center text-sm px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white font-medium rounded transition-colors"
                          >
                            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            {t('deploy.createEnvironment')}
                          </Link>
                        </div>
                      ) : (
                        <>
                          <select
                            {...register('environment_id', { required: t('deploy.selectEnvRequired') as string })}
                            className="w-full px-3 py-2 border border-border-1 bg-surface-3 text-text-primary rounded-md focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                            disabled={!watchProjectId}
                          >
                            <option value="">{t('deploy.selectEnv')}</option>
                            {environments.map((env) => (
                              <option key={env.id} value={env.id}>
                                {env.nom}
                              </option>
                            ))}
                          </select>
                          {errors.environment_id && (
                            <p className="mt-1 text-sm text-red-600">{errors.environment_id.message}</p>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Configuration & Deploiement (merged old steps 4+5) */}
          {step === 3 && (
            <div className="bg-surface-2 rounded-card shadow-card border border-border-1 p-6">
              <h2 className="text-2xl font-bold text-text-primary mb-6">{t('deploy.appConfig')}</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* App Name */}
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    {t('deploy.appNameLabel')}
                  </label>
                  <input
                    {...register('app_name', { required: t('deploy.appNameRequired') as string })}
                    type="text"
                    className="w-full px-3 py-2 border border-border-1 bg-surface-3 text-text-primary rounded-md focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                    placeholder="mon-app"
                  />
                  {errors.app_name && (
                    <p className="mt-1 text-sm text-red-600">{errors.app_name.message}</p>
                  )}
                </div>

                {/* Resource Configuration */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    {t('deploy.resources')}
                  </label>

                  {/* Information message for ERP applications */}
                  {appType === 'saas' && !APPS_VIA_ENDPOINT_MANAGE.includes(selectedTemplate?.type ?? '') && (
                    <div className="mb-3 p-2 bg-blue-500/12 border border-blue-500/30 rounded-md">
                      <p className="text-xs text-blue-300">
                        {t('deploy.resourcesNote')}
                      </p>
                    </div>
                  )}

                  <div className={`grid grid-cols-2 gap-4 ${APPS_VIA_ENDPOINT_MANAGE.includes(selectedTemplate?.type ?? '') ? 'md:grid-cols-3' : 'md:grid-cols-4'}`}>
                    <div>
                      <label className="block text-xs text-text-secondary mb-1">{t('deploy.cpuCoresLabel')}</label>
                      <input
                        {...register('cpu', {
                          required: t('deploy.cpuRequired') as string,
                          min: { value: 0.1, message: t('deploy.cpuMin') as string },
                          max: { value: 8, message: t('deploy.cpuMax') as string }
                        })}
                        type="number"
                        step="0.1"
                        min="0.1"
                        max="8"
                        defaultValue="0.5"
                        className="w-full px-2 py-1 border border-border-1 bg-surface-3 text-text-primary rounded-md text-sm focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                        placeholder="0.5"
                      />
                      {errors.cpu && (
                        <p className="text-xs text-red-600 mt-1">{errors.cpu.message}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs text-text-secondary mb-1">{t('deploy.ramGbLabel')}</label>
                      <input
                        {...register('ram', {
                          required: t('deploy.ramRequired') as string,
                          min: { value: 0.5, message: t('deploy.ramMin') as string },
                          max: { value: 16, message: t('deploy.ramMax') as string }
                        })}
                        type="number"
                        step="0.1"
                        min="0.5"
                        max="16"
                        defaultValue="1.0"
                        className="w-full px-2 py-1 border border-border-1 bg-surface-3 text-text-primary rounded-md text-sm focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                        placeholder="1.0"
                      />
                      {errors.ram && (
                        <p className="text-xs text-red-600 mt-1">{errors.ram.message}</p>
                      )}
                    </div>
                    {/* Keycloak ne monte aucun volume : son etat vit dans le
                        PostgreSQL hote. Demander un stockage laisserait croire
                        qu'il en consomme, et le champ serait ignore. */}
                    {!APPS_VIA_ENDPOINT_MANAGE.includes(selectedTemplate?.type ?? '') && (
                    <div>
                      <label className="block text-xs text-text-secondary mb-1">{t('deploy.storageGbLabel')}</label>
                      <input
                        {...register('storage', {
                          required: APPS_VIA_ENDPOINT_MANAGE.includes(selectedTemplate?.type ?? '')
                            ? false
                            : (t('deploy.storageRequired') as string),
                          min: { value: 1, message: t('deploy.storageMin') as string },
                          max: { value: 100, message: t('deploy.storageMax') as string }
                        })}
                        type="number"
                        min="1"
                        max="100"
                        defaultValue="20"
                        className="w-full px-2 py-1 border border-border-1 bg-surface-3 text-text-primary rounded-md text-sm focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                        placeholder="20"
                      />
                      {errors.storage && (
                        <p className="text-xs text-red-600 mt-1">{errors.storage.message}</p>
                      )}
                    </div>
                    )}
                    <div>
                      <label className="block text-xs text-text-secondary mb-1">{t('deploy.replicasLabel')}</label>
                      <input
                        {...register('replicas', {
                          required: t('deploy.replicasRequired') as string,
                          min: { value: 1, message: t('deploy.replicasMin') as string },
                          max: { value: 10, message: t('deploy.replicasMax') as string }
                        })}
                        type="number"
                        min="1"
                        max="10"
                        defaultValue="1"
                        className="w-full px-2 py-1 border border-border-1 bg-surface-3 text-text-primary rounded-md text-sm focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                        placeholder="1"
                      />
                      {errors.replicas && (
                        <p className="text-xs text-red-600 mt-1">{errors.replicas.message}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Conditional fields based on app type */}
                {appType === 'code' && (
                  <>
                    {/* Type de charge : web / worker / cron — choix prominent, en tête de la config */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-text-secondary mb-3">
                        {t('deploy.workloadType')} <span className="text-red-500">*</span>
                      </label>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {(['web', 'worker', 'cron'] as const).map((wt) => (
                          <button
                            type="button"
                            key={wt}
                            onClick={() => setWorkloadType(wt)}
                            className={`text-left p-4 border-2 rounded-lg transition-all ${
                              workloadType === wt
                                ? 'border-accent-500 bg-accent-500/12'
                                : 'border-border-1 hover:border-border-2'
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <SignalIcon className="w-5 h-5 text-accent-500" />
                              <span className="font-bold text-text-primary capitalize">{wt}</span>
                            </div>
                            <p className="text-xs text-text-tertiary">
                              {t(`deploy.workload${wt.charAt(0).toUpperCase() + wt.slice(1)}`)}
                            </p>
                          </button>
                        ))}
                      </div>
                      {/* CONSTRUCTEUR plutot que le champ brut. Il demandait les cinq champs
                          cron, et un utilisateur de console ecrit « 2***2* » : Kubernetes
                          refuse alors le CronJob, mais apres le 201 de l'API, donc l'erreur
                          n'apparait que dans ArgoCD. Le constructeur ne peut produire que des
                          expressions valides, et garde la saisie libre en dernier choix. */}
                      {workloadType === 'cron' && (
                        <div className="mt-4">
                          <label className="block text-sm font-medium text-text-secondary mb-2">{t('deploy.cronSchedule')}</label>
                          <CronScheduleBuilder value={cronSchedule} onChange={setCronSchedule} />
                        </div>
                      )}
                      <div className="mt-4">
                            <label className="block text-sm font-medium text-text-secondary mb-2">
                              {t('deploy.startCommand')}
                              {workloadType !== 'web' && <span className="text-red-500"> *</span>}
                            </label>
                            <input
                              type="text"
                              value={startCommand}
                              onChange={(e) => setStartCommand(e.target.value)}
                              placeholder="celery -A config worker --loglevel=info --concurrency=2"
                              className="w-full px-3 py-2 border border-border-1 bg-surface-3 text-text-primary rounded-md font-mono focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                            />
                            <p className="mt-2 text-xs text-text-tertiary">{t('deploy.startCommandHelp')}</p>
                            {/* APERCU DES JETONS. `parseCommand` ne gere que les guillemets
                                DOUBLES et ignore apostrophes et echappements : montrer le
                                decoupage obtenu est ce qui evite la mauvaise surprise de
                                quoting, decouverte sinon au premier CrashLoopBackOff. */}
                            {startCommand.trim() && (
                              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                                <span className="text-xs text-text-quaternary">{t('deploy.startCommandPreview')}</span>
                                {parseCommand(startCommand).map((jeton, i) => (
                                  <code
                                    key={i}
                                    className="px-1.5 py-0.5 rounded bg-surface-3 border border-border-1 text-xs font-mono text-text-secondary"
                                  >
                                    {jeton}
                                  </code>
                                ))}
                              </div>
                            )}
                      </div>
                      {workloadType !== 'web' && (
                        <p className="mt-3 text-xs text-yellow-300/90">{t('deploy.workloadNoHttp')}</p>
                      )}
                    </div>

                    {/* Image Source Selector */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-text-secondary mb-3">
                        {t('deploy.dockerImageSource')} <span className="text-red-500">*</span>
                      </label>
                      <div className="flex gap-4">
                        <label className={`flex-1 relative flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          imageSourceMode === 'builtin'
                            ? 'border-accent-500 bg-accent-500/12'
                            : 'border-border-1 hover:border-border-2'
                        }`}>
                          <input
                            type="radio"
                            name="imageSource"
                            value="builtin"
                            checked={imageSourceMode === 'builtin'}
                            onChange={() => {
                              setImageSourceMode('builtin');
                              setSelectedBuiltImageId(null);
                              setValue('docker_image_url', '');
                              setValue('docker_image_tag', '');
                            }}
                            className="sr-only"
                          />
                          <div className="flex items-center">
                            <ArchiveBoxIcon className="w-6 h-6 mr-3" />
                            <div>
                              <p className="font-medium text-text-primary">{t('deploy.nubiBuildImage')}</p>
                              <p className="text-sm text-text-tertiary">{t('deploy.nubiBuildImageDesc')}</p>
                            </div>
                          </div>
                          {imageSourceMode === 'builtin' && (
                            <div className="absolute top-2 right-2 text-accent-500">
                              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                            </div>
                          )}
                        </label>
                        <label className={`flex-1 relative flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          imageSourceMode === 'manual'
                            ? 'border-accent-500 bg-accent-500/12'
                            : 'border-border-1 hover:border-border-2'
                        }`}>
                          <input
                            type="radio"
                            name="imageSource"
                            value="manual"
                            checked={imageSourceMode === 'manual'}
                            onChange={() => {
                              setImageSourceMode('manual');
                              setSelectedBuiltImageId(null);
                              setValue('docker_image_url', '');
                              setValue('docker_image_tag', 'latest');
                            }}
                            className="sr-only"
                          />
                          <div className="flex items-center">
                            <PencilIcon className="w-6 h-6 mr-3" />
                            <div>
                              <p className="font-medium text-text-primary">{t('deploy.manualUrl')}</p>
                              <p className="text-sm text-text-tertiary">{t('deploy.manualUrlDesc')}</p>
                            </div>
                          </div>
                          {imageSourceMode === 'manual' && (
                            <div className="absolute top-2 right-2 text-accent-500">
                              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                            </div>
                          )}
                        </label>
                      </div>
                    </div>

                    {/* Built Images Selector */}
                    {imageSourceMode === 'builtin' && (
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-text-secondary mb-2">
                          {t('deploy.selectBuiltImage')} <span className="text-red-500">*</span>
                        </label>
                        {loadingImages ? (
                          <div className="flex items-center justify-center p-4 border border-border-1 rounded-md">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-accent-500 mr-2"></div>
                            <span className="text-text-tertiary">{t('deploy.loadingImages')}</span>
                          </div>
                        ) : builtImages.length === 0 ? (
                          <div className="p-4 bg-yellow-500/12 border border-yellow-500/30 rounded-md">
                            <div className="flex items-start">
                              <ExclamationTriangleIcon className="w-6 h-6 text-yellow-500 mr-3" />
                              <div className="text-sm text-yellow-300">
                                <p className="font-medium mb-1">{t('deploy.noBuiltImages')}</p>
                                <p>
                                  {t('deploy.noBuiltImagesDesc')}
                                  <Link to="/builds" className="ml-1 text-accent-400 hover:underline font-medium">
                                    {t('deploy.createFirstBuild')} →
                                  </Link>
                                </p>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <>
                            <select
                              value={selectedBuiltImageId || ''}
                              onChange={(e) => handleBuiltImageSelect(e.target.value)}
                              className="w-full px-3 py-2 border border-border-1 bg-surface-3 text-text-primary rounded-md focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                            >
                              <option value="">{t('deploy.selectAnImage')}</option>
                              {builtImages.map((image) => (
                                <option key={image.build_id} value={image.build_id}>
                                  {image.application_name} - {image.image_tag} ({image.project?.name || 'Standalone'})
                                </option>
                              ))}
                            </select>
                            {selectedBuiltImageId && (
                              <div className="mt-3 p-3 bg-green-500/12 border border-green-500/30 rounded-md">
                                <p className="text-sm text-green-300">
                                  <span className="font-medium">{t('deploy.selectedImage')}</span>
                                </p>
                                <p className="mt-1 font-mono text-xs text-green-300 break-all">
                                  {watch('docker_image_url')}:{watch('docker_image_tag')}
                                </p>
                              </div>
                            )}
                          </>
                        )}
                        <input type="hidden" {...register('docker_image_url', {
                          required: imageSourceMode === 'builtin' && !selectedBuiltImageId ? t('deploy.selectImageRequired') as string : false
                        })} />
                      </div>
                    )}

                    {/* Manual Docker Image URL */}
                    {imageSourceMode === 'manual' && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-text-secondary mb-2">
                            {t('deploy.dockerImageUrl')} <span className="text-red-500">*</span>
                          </label>
                          <input
                            {...register('docker_image_url', {
                              required: imageSourceMode === 'manual' ? t('deploy.dockerImageUrlRequiredError') as string : false
                            })}
                            type="text"
                            className="w-full px-3 py-2 border border-border-1 bg-surface-3 text-text-primary rounded-md focus:outline-none focus:ring-accent-500 focus:border-accent-500 font-mono text-sm"
                            placeholder="registry.gitlab.com/nubitech1/my-app"
                          />
                          {errors.docker_image_url && (
                            <p className="mt-1 text-sm text-red-600">{errors.docker_image_url.message}</p>
                          )}
                          <p className="mt-1 text-xs text-text-tertiary">
                            {t('deploy.dockerImageUrlHelp')}
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-text-secondary mb-2">
                            {t('deploy.imageTagLabel')}
                          </label>
                          <input
                            {...register('docker_image_tag')}
                            type="text"
                            defaultValue="latest"
                            className="w-full px-3 py-2 border border-border-1 bg-surface-3 text-text-primary rounded-md focus:outline-none focus:ring-accent-500 focus:border-accent-500 font-mono text-sm"
                            placeholder="latest"
                          />
                          <p className="mt-1 text-xs text-text-tertiary">
                            {t('deploy.imageTagHelp')}
                          </p>
                        </div>
                      </>
                    )}

                    {/* Environment Variables Editor - Always shown for Code apps */}
                    <div className="md:col-span-2">
                      <EnvironmentVariablesEditor
                        variables={environmentVariables}
                        onChange={setEnvironmentVariables}
                      />
                    </div>

                    {/* Sensitive Variables Editor (Secrets) */}
                    <div className="md:col-span-2">
                      <SensitiveVariablesEditor
                        variables={sensitiveVariables}
                        onChange={setSensitiveVariables}
                      />
                    </div>

                    {/* Expose Public Toggle — sans objet hors `web` : pas de Service, donc
                        l'Ingress rendu pointerait un backend inexistant. */}
                    {workloadType === 'web' && (
                    <div className="md:col-span-2">
                      <div className="bg-surface-2 rounded-lg border border-border-1 p-6">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-teal-600 rounded-lg flex items-center justify-center">
                              <GlobeAltIcon className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <h3 className="text-lg font-bold text-text-primary">
                                {t('deploy.exposePublic')}
                              </h3>
                              <p className="text-sm text-text-tertiary">
                                {t('deploy.exposePublicDesc')}
                              </p>
                            </div>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={exposePublic}
                              onChange={(e) => setExposePublic(e.target.checked)}
                              className="sr-only peer"
                            />
                            <div className="w-14 h-7 bg-surface-3 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-500/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-border-1 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-green-600"></div>
                            <span className="ml-3 text-sm font-medium text-text-primary">
                              {exposePublic ? t('deploy.enabled') : t('deploy.disabled')}
                            </span>
                          </label>
                        </div>
                        {exposePublic ? (
                          <div className="mt-4 p-3 bg-green-500/12 rounded-md">
                            <p className="text-sm text-green-300">
                              <CheckCircleIcon className="w-4 h-4 inline mr-1" /> {t('deploy.exposePublicEnabled')}
                            </p>
                          </div>
                        ) : (
                          <div className="mt-4 p-3 bg-yellow-500/12 rounded-md">
                            <p className="text-sm text-yellow-300">
                              <LockClosedIcon className="w-4 h-4 inline mr-1" /> {t('deploy.exposePublicDisabled')}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                    )}

                    {/* Port de l'application */}
                    {workloadType === 'web' && (
                    <div>
                      <div className="bg-surface-2 rounded-lg border border-border-1 p-6">
                        <div className="flex items-center space-x-3 mb-4">
                          <div className="w-10 h-10 bg-gradient-to-br from-accent-500 to-accent-600 rounded-lg flex items-center justify-center">
                            <SignalIcon className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <h3 className="text-lg font-bold text-text-primary">
                              {t('deploy.appPort')}
                            </h3>
                            <p className="text-sm text-text-tertiary">
                              {t('deploy.appPortDesc')}
                            </p>
                          </div>
                        </div>
                        <div>
                          <input
                            type="number"
                            value={appPort}
                            onChange={(e) => setAppPort(parseInt(e.target.value) || 8000)}
                            min="1"
                            max="65535"
                            className="w-full px-3 py-2 border border-border-1 bg-surface-3 text-text-primary rounded-md focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                          />
                          <p className="mt-2 text-xs text-text-tertiary">
                            {t('deploy.appPortHelp')}
                            <br />
                            <span className="text-text-quaternary">{t('deploy.appPortDefaults')}</span>
                          </p>
                        </div>
                      </div>
                    </div>
                    )}

                    {/* Health Check Path — sans objet hors `web` : le chart ne rend les sondes
                        que si un Service existe. */}
                    {workloadType === 'web' && (
                    <div>
                      <div className="bg-surface-2 rounded-lg border border-border-1 p-6">
                        <div className="flex items-center space-x-3 mb-4">
                          <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
                            <CheckCircleIcon className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <h3 className="text-lg font-bold text-text-primary">
                              {t('deploy.healthCheck')}
                            </h3>
                            <p className="text-sm text-text-tertiary">
                              {t('deploy.healthCheckDesc')}
                            </p>
                          </div>
                        </div>
                        <div>
                          <input
                            type="text"
                            value={healthCheckPath}
                            onChange={(e) => setHealthCheckPath(e.target.value)}
                            placeholder={t('deploy.healthCheckPlaceholder') as string}
                            className="w-full px-3 py-2 border border-border-1 bg-surface-3 text-text-primary rounded-md focus:outline-none focus:ring-green-500 focus:border-green-500"
                          />
                          <p className="mt-2 text-xs text-text-tertiary">
                            {t('deploy.healthCheckTcp')}
                            <br />
                            <span className="text-text-quaternary">{t('deploy.healthCheckHttp')}</span>
                          </p>
                        </div>
                      </div>
                    </div>
                    )}

                    {/* Nginx custom config : html-static (image contient nginx) + nextjs (sidecar auto-enabled) */}
                    {selectedTemplate && ['html-static', 'nextjs'].includes(selectedTemplate.type) && (
                      <div className="md:col-span-2">
                        <div className="bg-surface-2 rounded-lg border border-border-1 p-6">
                          <div className="flex items-center space-x-3 mb-4">
                            <div className="w-10 h-10 bg-gradient-to-br from-slate-500 to-slate-700 rounded-lg flex items-center justify-center">
                              <CodeBracketIcon className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <h3 className="text-lg font-bold text-text-primary">
                                {t('deploy.nginxCustomConfig')}
                              </h3>
                              <p className="text-sm text-text-tertiary">
                                {t('deploy.nginxCustomConfigDesc')}
                              </p>
                            </div>
                          </div>
                          <div>
                            <textarea
                              value={nginxCustomConfig}
                              onChange={(e) => setNginxCustomConfig(e.target.value)}
                              placeholder={t('deploy.nginxCustomConfigPlaceholder') as string}
                              rows={10}
                              className="w-full px-3 py-2 border border-border-1 bg-surface-3 text-text-primary rounded-md focus:outline-none focus:ring-slate-500 focus:border-slate-500 font-mono text-xs"
                              maxLength={32768}
                            />
                            <p className="mt-2 text-xs text-text-tertiary">
                              {t('deploy.nginxCustomConfigHelp')}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Migration Job Section */}
                    <div className="md:col-span-2">
                      <div className="bg-surface-2 rounded-lg border border-border-1 p-6">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-accent-500 to-accent-600 rounded-lg flex items-center justify-center">
                              <ArrowPathIcon className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <h3 className="text-lg font-bold text-text-primary">
                                {t('deploy.dbMigration')}
                              </h3>
                              <p className="text-sm text-text-tertiary">
                                {t('deploy.dbMigrationDesc')}
                              </p>
                            </div>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={migrationEnabled}
                              onChange={(e) => setMigrationEnabled(e.target.checked)}
                              className="sr-only peer"
                            />
                            <div className="w-14 h-7 bg-surface-3 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-500/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-border-1 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-purple-600"></div>
                            <span className="ml-3 text-sm font-medium text-text-primary">
                              {migrationEnabled ? t('deploy.enabled') : t('deploy.disabled')}
                            </span>
                          </label>
                        </div>

                        {migrationEnabled && (
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-text-secondary mb-2">
                                {t('deploy.migrationCommand')}
                              </label>
                              <input
                                type="text"
                                value={migrationCommand}
                                onChange={(e) => setMigrationCommand(e.target.value)}
                                className="w-full px-3 py-2 border border-border-1 bg-surface-3 text-text-primary rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                                placeholder="python manage.py migrate"
                              />
                              <p className="mt-1 text-xs text-text-tertiary">
                                {t('deploy.migrationExamples')} <code className="bg-surface-3 px-1 rounded">python manage.py migrate</code>, <code className="bg-surface-3 px-1 rounded">npx prisma migrate deploy</code>, <code className="bg-surface-3 px-1 rounded">alembic upgrade head</code>
                              </p>
                            </div>

                            <div className="flex items-center justify-between p-3 bg-surface-3 rounded-md">
                              <div>
                                <p className="text-sm font-medium text-text-secondary">
                                  {t('deploy.blockOnFailure')}
                                </p>
                                <p className="text-xs text-text-tertiary">
                                  {t('deploy.blockOnFailureDesc')}
                                </p>
                              </div>
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={migrationBlockOnFailure}
                                  onChange={(e) => setMigrationBlockOnFailure(e.target.checked)}
                                  className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-surface-3 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-500/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-border-1 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                              </label>
                            </div>

                            {/* Toggle "Image dediee a la migration" */}
                            <div className="flex items-center justify-between p-3 bg-surface-3 rounded-md">
                              <div>
                                <p className="text-sm font-medium text-text-primary">
                                  {t('deploy.migrationUseCustomImage')}
                                </p>
                                <p className="text-xs text-text-tertiary">
                                  {t('deploy.migrationUseCustomImageDesc')}
                                </p>
                              </div>
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={migrationUseCustomImage}
                                  onChange={(e) => {
                                    setMigrationUseCustomImage(e.target.checked);
                                    if (!e.target.checked) {
                                      setMigrationSelectedBuiltImageId(null);
                                      setMigrationImageUrl('');
                                      setMigrationImageTag('latest');
                                    }
                                  }}
                                  className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-surface-3 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-500/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-border-1 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                              </label>
                            </div>

                            {/* Sélecteur d'image de migration (visible si custom image activée) */}
                            {migrationUseCustomImage && (
                              <div className="space-y-3 pl-2 border-l-2 border-purple-500/30">
                                <label className="block text-sm font-medium text-text-secondary">
                                  {t('deploy.migrationImageSource')}
                                </label>
                                <div className="flex gap-3">
                                  <label className={`flex-1 flex items-center p-2 border rounded-md cursor-pointer text-sm ${
                                    migrationImageSourceMode === 'builtin'
                                      ? 'border-accent-500 bg-accent-500/12'
                                      : 'border-border-1 hover:border-border-2'
                                  }`}>
                                    <input
                                      type="radio"
                                      name="migrationImageSource"
                                      value="builtin"
                                      checked={migrationImageSourceMode === 'builtin'}
                                      onChange={() => {
                                        setMigrationImageSourceMode('builtin');
                                        setMigrationSelectedBuiltImageId(null);
                                        setMigrationImageUrl('');
                                        setMigrationImageTag('');
                                      }}
                                      className="mr-2"
                                    />
                                    {t('deploy.nubiBuildImage')}
                                  </label>
                                  <label className={`flex-1 flex items-center p-2 border rounded-md cursor-pointer text-sm ${
                                    migrationImageSourceMode === 'manual'
                                      ? 'border-accent-500 bg-accent-500/12'
                                      : 'border-border-1 hover:border-border-2'
                                  }`}>
                                    <input
                                      type="radio"
                                      name="migrationImageSource"
                                      value="manual"
                                      checked={migrationImageSourceMode === 'manual'}
                                      onChange={() => {
                                        setMigrationImageSourceMode('manual');
                                        setMigrationSelectedBuiltImageId(null);
                                        setMigrationImageUrl('');
                                        setMigrationImageTag('latest');
                                      }}
                                      className="mr-2"
                                    />
                                    {t('deploy.manualUrl')}
                                  </label>
                                </div>

                                {migrationImageSourceMode === 'builtin' ? (
                                  <select
                                    value={migrationSelectedBuiltImageId || ''}
                                    onChange={(e) => handleMigrationBuiltImageSelect(e.target.value)}
                                    className="w-full px-3 py-2 text-sm border border-border-1 bg-surface-3 text-text-primary rounded-md focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                                  >
                                    <option value="">{t('deploy.selectMigrationImage')}</option>
                                    {builtImages.map((image) => (
                                      <option key={image.build_id} value={image.build_id}>
                                        {image.application_name} - {image.image_tag} ({image.project?.name || 'Standalone'})
                                      </option>
                                    ))}
                                  </select>
                                ) : (
                                  <div className="space-y-2">
                                    <input
                                      type="text"
                                      value={migrationImageUrl}
                                      onChange={(e) => setMigrationImageUrl(e.target.value)}
                                      placeholder="registry.gitlab.com/username/project"
                                      className="w-full px-3 py-2 text-sm border border-border-1 bg-surface-3 text-text-primary rounded-md focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                                    />
                                    <input
                                      type="text"
                                      value={migrationImageTag}
                                      onChange={(e) => setMigrationImageTag(e.target.value)}
                                      placeholder="latest"
                                      className="w-full px-3 py-2 text-sm border border-border-1 bg-surface-3 text-text-primary rounded-md focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                                    />
                                  </div>
                                )}
                              </div>
                            )}

                            <div className="p-3 bg-purple-500/12 rounded-md">
                              <p className="text-sm text-purple-300">
                                <InformationCircleIcon className="w-4 h-4 inline mr-1" /> {t('deploy.migrationAutoRun')}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Custom Domain — sans objet hors `web` : un domaine suppose un Ingress,
                        donc un Service, qu'un worker ou un cron n'a pas. */}
                    {workloadType === 'web' && (
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-text-secondary mb-2">
                        {t('deploy.customDomain')}
                      </label>
                      <input
                        {...register('custom_domain')}
                        type="text"
                        className="w-full px-3 py-2 border border-border-1 bg-surface-3 text-text-primary rounded-md focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                        placeholder="monsite.com"
                      />
                      <div className="mt-2 p-3 bg-blue-500/12 border border-blue-500/30 rounded-md">
                        <p className="text-xs text-blue-300 font-medium mb-1">
                          {t('deploy.dnsRequired')}
                        </p>
                        <p className="text-xs text-blue-400">
                          {t('deploy.dnsInstruction')}
                        </p>
                        <code className="block mt-1 text-xs bg-blue-500/12 text-blue-300 px-2 py-1 rounded font-mono">
                          votre-domaine.com → zone-nubi-1.nubiecloud.io
                        </code>
                      </div>
                    </div>
                    )}
                  </>
                )}

                {appType === 'saas' && !APPS_VIA_ENDPOINT_MANAGE.includes(selectedTemplate?.type ?? '') && (
                  <>
                    {/* Configuration spécifique à Odoo */}
                    {selectedTemplate?.type === 'odoo' && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-text-secondary mb-2">
                            {t('deploy.odooAdminEmail')}
                          </label>
                          <input
                            {...register('odoo_email')}
                            type="email"
                            className="w-full px-3 py-2 border border-border-1 bg-surface-3 text-text-primary rounded-md focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                            placeholder="admin@example.com"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-text-secondary mb-2">
                            {t('deploy.odooAdminPassword')} <span className="text-red-500">*</span>
                          </label>
                          <input
                            {...register('odoo_password', {
                              required: t('deploy.odooPasswordRequired') as string,
                              minLength: { value: 8, message: t('deploy.minChars', { count: 8 }) as string }
                            })}
                            type="password"
                            className="w-full px-3 py-2 border border-border-1 bg-surface-3 text-text-primary rounded-md focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                            placeholder={t('deploy.securePasswordPlaceholder') as string}
                          />
                          {errors.odoo_password && (
                            <p className="mt-1 text-sm text-red-600">{errors.odoo_password.message}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-text-secondary mb-2">
                            {t('deploy.postgresPassword')} <span className="text-red-500">*</span>
                          </label>
                          <input
                            {...register('postgres_password', {
                              required: t('deploy.postgresPasswordRequired') as string,
                              minLength: { value: 8, message: t('deploy.minChars', { count: 8 }) as string }
                            })}
                            type="password"
                            className="w-full px-3 py-2 border border-border-1 bg-surface-3 text-text-primary rounded-md focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                            placeholder={t('deploy.securePasswordPlaceholder') as string}
                          />
                          {errors.postgres_password && (
                            <p className="mt-1 text-sm text-red-600">{errors.postgres_password.message}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-text-secondary mb-2">
                            {t('deploy.odooVersion')}
                          </label>
                          <select
                            {...register('image_tag')}
                            className="w-full px-3 py-2 border border-border-1 bg-surface-3 text-text-primary rounded-md focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                            defaultValue="17.0"
                          >
                            <option value="19.0">Odoo 19.0</option>
                            <option value="18.0">Odoo 18.0</option>
                            <option value="17.0">Odoo 17.0</option>
                            <option value="16.0">Odoo 16.0</option>
                            <option value="15.0">Odoo 15.0</option>
                          </select>
                          <p className="mt-1 text-xs text-text-tertiary">
                            {t('deploy.odooVersionHelp')}
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-text-secondary mb-2">
                            {t('deploy.language')}
                          </label>
                          <select
                            {...register('odoo_language')}
                            className="w-full px-3 py-2 border border-border-1 bg-surface-3 text-text-primary rounded-md focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                            defaultValue="fr_FR"
                          >
                            <option value="fr_FR">Français</option>
                            <option value="en_US">English</option>
                            <option value="es_ES">Español</option>
                            <option value="de_DE">Deutsch</option>
                            <option value="it_IT">Italiano</option>
                          </select>
                          <p className="mt-1 text-xs text-text-tertiary">
                            {t('deploy.odooLanguageHelp')}
                          </p>
                        </div>
                      </>
                    )}

                    {/* Configuration spécifique à WordPress */}
                    {selectedTemplate?.type === 'wordpress' && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-text-secondary mb-2">
                            {t('deploy.wpAdminEmail')}
                          </label>
                          <input
                            {...register('wordpress_email')}
                            type="email"
                            className="w-full px-3 py-2 border border-border-1 bg-surface-3 text-text-primary rounded-md focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                            placeholder="admin@nubitech.io"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-text-secondary mb-2">
                            {t('deploy.wpAdminPassword')} <span className="text-red-500">*</span>
                          </label>
                          <input
                            {...register('wordpress_password', {
                              required: t('deploy.wpPasswordRequired') as string,
                              minLength: { value: 8, message: t('deploy.minChars', { count: 8 }) as string }
                            })}
                            type="password"
                            className="w-full px-3 py-2 border border-border-1 bg-surface-3 text-text-primary rounded-md focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                            placeholder={t('deploy.securePasswordPlaceholder') as string}
                          />
                          {errors.wordpress_password && (
                            <p className="mt-1 text-sm text-red-600">{errors.wordpress_password.message}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-text-secondary mb-2">
                            {t('deploy.mariadbRootPassword')} <span className="text-red-500">*</span>
                          </label>
                          <input
                            {...register('mariadb_root_password', {
                              required: t('deploy.mariadbRootPasswordRequired') as string,
                              minLength: { value: 8, message: t('deploy.minChars', { count: 8 }) as string }
                            })}
                            type="password"
                            className="w-full px-3 py-2 border border-border-1 bg-surface-3 text-text-primary rounded-md focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                            placeholder={t('deploy.securePasswordPlaceholder') as string}
                          />
                          {errors.mariadb_root_password && (
                            <p className="mt-1 text-sm text-red-600">{errors.mariadb_root_password.message}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-text-secondary mb-2">
                            {t('deploy.mariadbUserPassword')} <span className="text-red-500">*</span>
                          </label>
                          <input
                            {...register('mariadb_user_password', {
                              required: t('deploy.mariadbUserPasswordRequired') as string,
                              minLength: { value: 8, message: t('deploy.minChars', { count: 8 }) as string }
                            })}
                            type="password"
                            className="w-full px-3 py-2 border border-border-1 bg-surface-3 text-text-primary rounded-md focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                            placeholder={t('deploy.securePasswordPlaceholder') as string}
                          />
                          {errors.mariadb_user_password && (
                            <p className="mt-1 text-sm text-red-600">{errors.mariadb_user_password.message}</p>
                          )}
                        </div>
                      </>
                    )}

                    {/* Configuration générale pour autres apps SaaS */}
                    {selectedTemplate?.type !== 'odoo' && selectedTemplate?.type !== 'wordpress' && (
                      <div>
                        <label className="block text-sm font-medium text-text-secondary mb-2">
                          {t('deploy.adminPassword')}
                        </label>
                        <input
                          {...register('admin_password')}
                          type="password"
                          className="w-full px-3 py-2 border border-border-1 bg-surface-3 text-text-primary rounded-md focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                          placeholder={t('deploy.securePasswordShortPlaceholder') as string}
                        />
                      </div>
                    )}

                    {/* Custom Domain */}
                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-2">
                        {t('deploy.customDomain')}
                      </label>
                      <input
                        {...register('custom_domain')}
                        type="text"
                        className="w-full px-3 py-2 border border-border-1 bg-surface-3 text-text-primary rounded-md focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                        placeholder="monsite.com"
                      />
                      <div className="mt-2 p-3 bg-blue-500/12 border border-blue-500/30 rounded-md">
                        <p className="text-xs text-blue-300 font-medium mb-1">
                          {t('deploy.dnsRequired')}
                        </p>
                        <p className="text-xs text-blue-400">
                          {t('deploy.dnsInstruction')}
                        </p>
                        <code className="block mt-1 text-xs bg-blue-500/12 text-blue-300 px-2 py-1 rounded font-mono">
                          votre-domaine.com → zone-nubi-1.nubiecloud.io
                        </code>
                      </div>
                    </div>
                  </>
                )}

                {usesManagedEndpoint && selectedTemplate && (
                  <>
                    {/* Info box for managed services */}
                    <div className="md:col-span-2">
                      <div className="p-4 bg-purple-500/12 rounded-lg border border-purple-500/30">
                        <div className="flex items-start">
                          <span className="text-2xl mr-3">{selectedTemplate.icon}</span>
                          <div className="text-sm text-purple-300">
                            <p className="font-medium mb-1">{t('deploy.managedServiceInfo', { title: selectedTemplate.title })}</p>
                            <p>
                              {t('deploy.managedServiceDesc')}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Keycloak : base hote + domaine. Pas de mot de passe saisi —
                        l'admin est genere par la plateforme et montre une fois. */}
                    {APPS_VIA_ENDPOINT_MANAGE.includes(selectedTemplate.type) && (
                      <>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-text-secondary mb-2">
                            {t('deploy.keycloakHostDatabase')} <span className="text-red-500">*</span>
                          </label>
                          <p className="mb-2 text-xs text-text-tertiary">
                            {selectedTemplate.type === 'keycloak'
                              ? t('deploy.keycloakHostDatabaseHelp')
                              : t('deploy.managedAppHostDatabaseHelp')}
                          </p>
                          {loadingKeycloakDatabases ? (
                            <p className="text-sm text-text-tertiary">{t('deploy.keycloakLoadingDatabases')}</p>
                          ) : keycloakDatabasesError ? (
                            <div className="p-3 bg-red-500/12 border border-red-500/30 rounded-md text-sm text-red-300">
                              {t('deploy.keycloakDatabasesError')}
                            </div>
                          ) : (
                            <>
                              {/* Plus de champ obligatoire ni d'impasse : laisse
                                  vide, la plateforme cree le PostgreSQL. L'etat
                                  « aucune base » n'est donc plus un blocage mais
                                  le cas par defaut. */}
                              <select
                                {...register('database_deployment_id')}
                                className="w-full px-3 py-2 border border-border-1 bg-surface-3 text-text-primary rounded-md focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                              >
                                <option value="">{t('deploy.keycloakAutoDatabase')}</option>
                                {/* Les inéligibles restent VISIBLES, désactivées et
                                    motivées : un PostgreSQL qui disparaît sans
                                    explication est un bug du point de vue de
                                    l'utilisateur, qui le voit dans sa console. */}
                                {keycloakDatabases.map((pgdb) => (
                                  <option
                                    key={pgdb.deployment_id}
                                    value={pgdb.deployment_id}
                                    disabled={!pgdb.eligible}
                                  >
                                    {pgdb.eligible ? pgdb.nom : `${pgdb.nom} — ${pgdb.reason ?? ''}`}
                                  </option>
                                ))}
                              </select>
                              <p className="mt-1 text-xs text-text-tertiary">
                                {keycloakDatabases.length === 0
                                  ? t('deploy.keycloakAutoDatabaseOnly')
                                  : t('deploy.keycloakAutoDatabaseHelp', {
                                      // « rattache a ce Keycloak » etait code en
                                      // dur : faux pour quatre applications sur
                                      // cinq.
                                      app: selectedTemplate?.title ?? '',
                                    })}
                              </p>
                              {/* Reutiliser un PostgreSQL existant a deux
                                  consequences que rien d'autre n'annonce, et
                                  dont la premiere detruit des donnees en
                                  silence : CNPG restaure un CLUSTER, pas une
                                  base. */}
                              {watch('database_deployment_id') && (
                                <div className="mt-2 p-3 bg-yellow-500/12 border border-yellow-500/30 rounded-md">
                                  {/* Meme correction que `keycloakAutoDatabaseHelp`
                                      juste au-dessus : ces trois textes nommaient
                                      « Keycloak » en dur, et s'affichaient tels quels
                                      devant un Open WebUI ou un n8n. Le nom vient du
                                      catalogue, comme partout ailleurs. */}
                                  <p className="text-xs text-yellow-300 font-medium mb-1">
                                    {t('deploy.keycloakSharedDbTitle', { app: selectedTemplate?.title ?? '' })}
                                  </p>
                                  <ul className="text-xs text-yellow-300/90 list-disc list-inside space-y-1">
                                    <li>{t('deploy.keycloakSharedDbRestore', { app: selectedTemplate?.title ?? '' })}</li>
                                    <li>{t('deploy.keycloakSharedDbDelete', { app: selectedTemplate?.title ?? '' })}</li>
                                  </ul>
                                </div>
                              )}
                            </>
                          )}
                        </div>

                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-text-secondary mb-2">
                            {t('deploy.keycloakCustomDomain')}
                          </label>
                          <input
                            {...register('custom_domain')}
                            type="text"
                            className="w-full px-3 py-2 border border-border-1 bg-surface-3 text-text-primary rounded-md focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                            placeholder="auth.mondomaine.com"
                          />
                          {/* L'issuer OIDC est grave dans chaque jeton emis : un
                              domaine personnalise n'est qu'un alias servi par le
                              meme Ingress, il ne le deplace pas. Le dire ici
                              evite que le client configure ses clients dessus. */}
                          <p className="mt-1 text-xs text-text-tertiary">
                            {selectedTemplate.type === 'keycloak'
                              ? t('deploy.keycloakCustomDomainHelp')
                              : t('deploy.managedAppCustomDomainHelp')}
                          </p>
                          {/* Le CNAME manquait ici, alors qu'il est affiche pour
                              les apps CODE et les SaaS : sans lui, le client
                              renseigne un domaine qui ne resoudra jamais, et le
                              certificat ne sera pas emis — sans que rien ne le
                              dise. Meme bloc, memes cles i18n. */}
                          <div className="mt-2 p-3 bg-blue-500/12 border border-blue-500/30 rounded-md">
                            <p className="text-xs text-blue-300 font-medium mb-1">
                              {t('deploy.dnsRequired')}
                            </p>
                            <p className="text-xs text-blue-400">
                              {t('deploy.dnsInstruction')}
                            </p>
                            <code className="block mt-1 text-xs bg-blue-500/12 text-blue-300 px-2 py-1 rounded font-mono">
                              votre-domaine.com → zone-nubi-1.nubiecloud.io
                            </code>
                          </div>
                        </div>
                      </>
                    )}

                    {/* Root Password — masque pour Kafka (Strimzi ne gere qu'un
                        utilisateur SCRAM, _generate_kafka_values ignore ce champ)
                        et pour Keycloak (son admin est genere par la plateforme).
                        L'afficher en obligatoire demanderait au client une valeur
                        qui ne servirait a rien. */}
                    {!['kafka', ...APPS_VIA_ENDPOINT_MANAGE].includes(selectedTemplate.type) && (

                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-2">
                        {t('deploy.rootPassword')} <span className="text-red-500">*</span>
                      </label>
                      <input
                        {...register('root_password', {
                          required: !['kafka', ...APPS_VIA_ENDPOINT_MANAGE].includes(selectedTemplate.type)
                            ? (t('deploy.rootPasswordRequired') as string)
                            : false,
                          minLength: { value: 8, message: t('deploy.minChars', { count: 8 }) as string }
                        })}
                        type="password"
                        className="w-full px-3 py-2 border border-border-1 bg-surface-3 text-text-primary rounded-md focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                        placeholder={t('deploy.securePasswordPlaceholder') as string}
                      />
                      {errors.root_password && (
                        <p className="mt-1 text-sm text-red-600">{errors.root_password.message}</p>
                      )}
                    </div>
                    )}

                    {/* Champs credentials — DBs (PostgreSQL, MySQL) et brokers (RabbitMQ, Kafka) */}
                    {['postgresql', 'mysql', 'rabbitmq', 'redis', 'kafka'].includes(selectedTemplate.type) && (
                      <>
                        {/* Nom de base : sans objet pour Redis, RabbitMQ et Kafka */}
                        {['postgresql', 'mysql'].includes(selectedTemplate.type) && (
                          <div>
                            <label className="block text-sm font-medium text-text-secondary mb-2">
                              {t('deploy.databaseName')}
                            </label>
                            <input
                              {...register('database')}
                              type="text"
                              defaultValue="app_db"
                              className="w-full px-3 py-2 border border-border-1 bg-surface-3 text-text-primary rounded-md focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                              placeholder="app_db"
                            />
                          </div>
                        )}

                        <div>
                          <label className="block text-sm font-medium text-text-secondary mb-2">
                            {t('deploy.username')}
                          </label>
                          <input
                            {...register('username')}
                            type="text"
                            defaultValue="app_user"
                            className="w-full px-3 py-2 border border-border-1 bg-surface-3 text-text-primary rounded-md focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                            placeholder="app_user"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-text-secondary mb-2">
                            {t('deploy.userPassword')} <span className="text-red-500">*</span>
                          </label>
                          <input
                            {...register('password', {
                              required: ['postgresql', 'mysql', 'rabbitmq', 'redis', 'kafka'].includes(selectedTemplate.type) ? t('deploy.userPasswordRequired') as string : false,
                              minLength: { value: 8, message: t('deploy.minChars', { count: 8 }) as string }
                            })}
                            type="password"
                            className="w-full px-3 py-2 border border-border-1 bg-surface-3 text-text-primary rounded-md focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                            placeholder={t('deploy.userPasswordPlaceholder') as string}
                          />
                          {errors.password && (
                            <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                          )}
                        </div>
                      </>
                    )}

                    {/* Extensions (PostgreSQL uniquement) */}
                    {selectedTemplate.type === 'postgresql' && (
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-text-secondary mb-2">
                          {t('deploy.pgExtensions')}
                        </label>
                        <div className="flex flex-wrap gap-4">
                          {[
                            { id: 'postgis', label: 'PostGIS', hint: t('deploy.pgExtPostgis') as string },
                            { id: 'pgvector', label: 'pgvector', hint: t('deploy.pgExtPgvector') as string },
                          ].map((ext) => (
                            <label key={ext.id} className="flex items-center space-x-2 cursor-pointer" title={ext.hint}>
                              <input
                                type="checkbox"
                                checked={pgExtensions.includes(ext.id)}
                                onChange={() => togglePgExtension(ext.id)}
                                className="rounded border-border-1 text-accent-600 focus:ring-accent-500"
                              />
                              <span className="text-sm text-text-primary">{ext.label}</span>
                            </label>
                          ))}
                        </div>
                        <p className="mt-1 text-xs text-text-tertiary">{t('deploy.pgExtensionsHelp')}</p>
                      </div>
                    )}

                    {/* Backup option for PostgreSQL */}
                    {selectedTemplate.type === 'postgresql' && (
                      <div className="md:col-span-2">
                        <label className="flex items-center space-x-2">
                          <input
                            {...register('enable_backups')}
                            type="checkbox"
                            className="rounded border-border-1 text-accent-600 focus:ring-accent-500"
                          />
                          <span className="text-sm font-medium text-text-secondary">
                            {t('deploy.enableS3Backups')}
                          </span>
                        </label>
                        <p className="mt-1 ml-6 text-xs text-text-tertiary">
                          {t('deploy.backupRetention')}
                        </p>
                      </div>
                    )}

                    {/* Bases additionnelles (PostgreSQL uniquement) */}
                    {selectedTemplate.type === 'postgresql' && (
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-text-secondary mb-2">
                          {t('deploy.additionalDatabases')}
                        </label>
                        <p className="mb-3 text-xs text-text-tertiary">
                          {t('deploy.additionalDatabasesHelp')}
                        </p>
                        <p className="mb-3 text-xs text-text-tertiary">
                          {t('deploy.additionalDatabasesNameHint')}
                        </p>
                        <div className="space-y-2">
                          {additionalDbFields.map((field, index) => (
                            <div key={field.id} className="flex items-start gap-2 p-3 bg-surface-0 rounded-md border border-border-1">
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 flex-1">
                                {/* Les 3 champs sont valides cote client sur les memes regles
                                    que le backend (cf. utils/pgIdentifier.ts). Sans affichage
                                    inline, react-hook-form bloquait le submit en silence. */}
                                <div>
                                  <input
                                    {...register(`additional_databases.${index}.name` as const, {
                                      pattern: {
                                        value: PG_IDENTIFIER_RE,
                                        message: t('deploy.additionalDatabasesInvalidName') as string,
                                      },
                                    })}
                                    type="text"
                                    placeholder={t('deploy.additionalDatabasesNamePlaceholder') as string}
                                    className="w-full px-3 py-2 border border-border-1 bg-surface-3 text-text-primary rounded-md text-sm focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                                  />
                                  {errors.additional_databases?.[index]?.name && (
                                    <p className="mt-1 text-xs text-red-600">{errors.additional_databases[index]?.name?.message}</p>
                                  )}
                                </div>
                                <div>
                                  <input
                                    {...register(`additional_databases.${index}.user` as const, {
                                      pattern: {
                                        value: PG_IDENTIFIER_RE,
                                        message: t('deploy.additionalDatabasesInvalidName') as string,
                                      },
                                    })}
                                    type="text"
                                    placeholder={t('deploy.additionalDatabasesUserPlaceholder') as string}
                                    className="w-full px-3 py-2 border border-border-1 bg-surface-3 text-text-primary rounded-md text-sm focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                                  />
                                  {errors.additional_databases?.[index]?.user && (
                                    <p className="mt-1 text-xs text-red-600">{errors.additional_databases[index]?.user?.message}</p>
                                  )}
                                </div>
                                <div>
                                  <input
                                    {...register(`additional_databases.${index}.password` as const, {
                                      validate: (value?: string) =>
                                        !value || isValidPgPassword(value) ||
                                        (t('deploy.additionalDatabasesInvalidPassword') as string),
                                    })}
                                    type="password"
                                    placeholder={t('deploy.additionalDatabasesPasswordPlaceholder') as string}
                                    className="w-full px-3 py-2 border border-border-1 bg-surface-3 text-text-primary rounded-md text-sm focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                                  />
                                  {errors.additional_databases?.[index]?.password && (
                                    <p className="mt-1 text-xs text-red-600">{errors.additional_databases[index]?.password?.message}</p>
                                  )}
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeAdditionalDb(index)}
                                className="px-3 py-2 text-red-400 hover:text-red-300 text-sm"
                                title={t('deploy.additionalDatabasesRemove') as string}
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => appendAdditionalDb({ name: '', user: '', password: '' })}
                            className="w-full px-3 py-2 border border-dashed border-border-1 text-text-secondary rounded-md text-sm hover:bg-surface-3"
                          >
                            + {t('deploy.additionalDatabasesAdd')}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Connection info preview */}
                    <div className="md:col-span-2">
                      {/* Coordonnées de connexion : pour un SERVICE qu'on branche
                          (hôte, port, base). Une application managée s'ouvre dans
                          un navigateur — afficher « Port: 5672 » devant un n8n
                          n'a aucun sens, et c'est le port de RabbitMQ. */}
                      <div className={`p-4 bg-surface-0 rounded-lg border border-border-1 ${
                        APPS_VIA_ENDPOINT_MANAGE.includes(selectedTemplate.type) ? 'hidden' : ''
                      }`}>
                        <p className="text-sm font-medium text-text-secondary mb-2">
                          <SignalIcon className="w-4 h-4 inline mr-1" /> {t('deploy.connectionInfo')}
                        </p>
                        <div className="text-xs font-mono text-text-secondary space-y-1">
                          <p>{t('deploy.hostAvailableAfterDeploy')}</p>
                          <p>Port: {({ postgresql: '5432', mysql: '3306', redis: '6379', rabbitmq: '5672', kafka: '9092' } as Record<string, string>)[selectedTemplate.type] ?? '5672'}</p>
                          {['postgresql', 'mysql'].includes(selectedTemplate.type) && (
                            <>
                              <p>Database: {`{database || 'app_db'}`}</p>
                              <p>Username: {`{username || 'app_user'}`}</p>
                            </>
                          )}
                          {selectedTemplate.type === 'kafka' && (
                            <>
                              <p>SASL: SCRAM-SHA-512</p>
                              <p>{t('deploy.kafkaUsernameNote')}</p>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8">
            <div>
              {step > 1 ? (
                <button
                  type="button"
                  onClick={() => {
                    if (step === 2 && environmentIdFromUrl && watch('environment_id')) {
                      setStep(1);
                    } else {
                      setStep(step - 1);
                    }
                  }}
                  className="bg-surface-3 text-text-secondary px-6 py-3 rounded-md font-medium hover:bg-surface-4 transition-colors"
                >
                  {`← ${t('deploy.previous')}`}
                </button>
              ) : (
                <Link
                  to={environmentIdFromUrl ? `/deployments/${environmentIdFromUrl}` : "/dashboard"}
                  className="bg-surface-3 text-text-secondary px-6 py-3 rounded-md font-medium hover:bg-surface-4 transition-colors inline-block"
                >
                  {`← ${t('deploy.back')}`}
                </Link>
              )}
            </div>
            <div>
              {step < 3 ? (
                <button
                  type="button"
                  onClick={() => {
                    if (step === 1 && environmentIdFromUrl && watch('environment_id')) {
                      setStep(3);
                    } else {
                      setStep(step + 1);
                    }
                  }}
                  disabled={
                    (step === 1 && (!appType || !selectedTemplate)) ||
                    (step === 2 && (!watchProjectId || !watch('environment_id')))
                  }
                  className="bg-accent-500 text-white px-6 py-3 rounded-md font-medium hover:bg-accent-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {`${t('deploy.continue')} →`}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleDeployClick}
                  disabled={deploying || deploymentSuccess || !watch('app_name') || (appType === 'code' && !watch('docker_image_url'))}
                  className={`px-8 py-3 rounded-md font-medium transition-all duration-300 ${
                    deploymentSuccess
                      ? 'bg-green-500 text-white'
                      : deploying
                        ? 'bg-blue-500 text-white'
                        : 'bg-green-600 text-white hover:bg-green-700'
                  } disabled:cursor-not-allowed disabled:opacity-50`}
                >
                  {deploymentSuccess ? (
                    <>
                      <svg className="inline w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                      </svg>
                      {t('deploy.deployedSuccess')}
                    </>
                  ) : deploying ? (
                    <>
                      <svg className="inline w-5 h-5 mr-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                      </svg>
                      {t('deploy.deployingInProgress')}
                    </>
                  ) : (
                    t('deploy.deploy')
                  )}
                </button>
              )}
            </div>
          </div>
        </form>

      {/* Keycloak : restitution UNIQUE des identifiants administrateur.
          Bloquant a dessein — la redirection automatique est suspendue tant que
          l'utilisateur n'a pas confirme les avoir notes. Le mot de passe n'est
          stocke en clair nulle part cote API : cet ecran est la seule occasion
          de le lire. */}
      {(keycloakCredentials || databaseCredentials) && (
        <div className="fixed inset-0 bg-black/60 overflow-y-auto h-full w-full z-50">
          <div className="relative top-16 mx-auto p-6 border border-border-1 w-full max-w-lg shadow-lg rounded-md bg-surface-2">
            <div className="flex items-center mb-4">
              <div className="flex items-center justify-center h-10 w-10 rounded-full bg-sky-500/20 mr-3">
                <KeyIcon className="w-5 h-5 text-sky-400" />
              </div>
              <h3 className="text-lg font-semibold text-text-primary">
                {!keycloakCredentials
                  ? t('deploy.databaseCredentialsTitle')
                  : keycloakCredentials.kind === 'protection'
                    ? t('deploy.setupAccessTitle')
                    : t('deploy.keycloakCredentialsTitle')}
              </h3>
            </div>

            {/* Le secret restitue n'a pas la meme nature selon l'application :
                pour Keycloak c'est le compte administrateur, pour n8n et
                Metabase c'est une porte temporaire devant une application qui
                n'a encore AUCUN compte. Les presenter pareil ferait croire au
                client qu'il tient ses identifiants definitifs. */}
            {keycloakCredentials?.kind === 'protection' && (
              <div className="p-3 mb-4 bg-sky-500/12 border border-sky-500/30 rounded-md">
                <p className="text-sm text-sky-300">{t('deploy.setupAccessExplain')}</p>
              </div>
            )}

            <div className="p-3 mb-4 bg-red-500/12 border border-red-500/30 rounded-md">
              <p className="text-sm text-red-300">{t('deploy.keycloakCredentialsWarning')}</p>
            </div>

            {keycloakCredentials && (
              <div className="space-y-3">
                {[
                  {
                    label: (keycloakCredentials.kind === 'protection'
                      ? t('deploy.setupAccessUrl')
                      : t('deploy.keycloakConsoleUrl')) as string,
                    value: keycloakCredentials.console_url,
                  },
                  // Ligne omise quand l'application n'a pas d'identifiant : Hasura
                  // s'authentifie avec le seul secret, et afficher « admin »
                  // enverrait le client chercher une saisie inexistante.
                  ...(keycloakCredentials.username
                    ? [{ label: t('deploy.username') as string, value: keycloakCredentials.username }]
                    : []),
                  { label: t('deploy.keycloakAdminPassword') as string, value: keycloakCredentials.password },
                ].map((field) => (
                  <CredentialRow key={field.label} label={field.label} value={field.value} copyLabel={t('deploy.keycloakCopy') as string} />
                ))}
              </div>
            )}

            {/* Base dediee sur le PostgreSQL hote. Le client voyait apparaitre
                une base et un role sur son PostgreSQL sans aucun moyen de s'y
                connecter : le mot de passe n'est relu nulle part ensuite. */}
            {databaseCredentials && (
              <div className={keycloakCredentials ? 'mt-5 pt-4 border-t border-border-1' : ''}>
                {keycloakCredentials && (
                  <h4 className="text-sm font-semibold text-text-primary mb-1">{t('deploy.databaseCredentialsTitle')}</h4>
                )}
                <p className="text-xs text-text-tertiary mb-3">
                  {t('deploy.databaseCredentialsExplain', { host: databaseCredentials.host_deployment_nom })}
                </p>
                <div className="space-y-3">
                  {[
                    { label: t('deploy.databaseHost') as string, value: databaseCredentials.host },
                    { label: t('deploy.databasePort') as string, value: databaseCredentials.port },
                    { label: t('deploy.databaseName') as string, value: databaseCredentials.name },
                    { label: t('deploy.username') as string, value: databaseCredentials.username },
                    { label: t('deploy.keycloakAdminPassword') as string, value: databaseCredentials.password },
                    {
                      label: t('deploy.databaseUrl') as string,
                      value: `postgresql://${databaseCredentials.username}:${databaseCredentials.password}@${databaseCredentials.host}:${databaseCredentials.port}/${databaseCredentials.name}`,
                    },
                  ].map((field) => (
                    <CredentialRow key={field.label} label={field.label} value={field.value} copyLabel={t('deploy.keycloakCopy') as string} />
                  ))}
                </div>
              </div>
            )}

            <p className="mt-4 text-xs text-text-tertiary">
              {!keycloakCredentials
                ? t('deploy.databaseCredentialsHint')
                : keycloakCredentials.kind === 'protection'
                  ? t('deploy.setupAccessHint')
                  : t('deploy.keycloakCredentialsHint')}
            </p>

            <label className="mt-4 flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={credentialsAcknowledged}
                onChange={(e) => setCredentialsAcknowledged(e.target.checked)}
                className="rounded border-border-1 text-accent-600 focus:ring-accent-500"
              />
              <span className="text-sm text-text-primary">{t('deploy.keycloakCredentialsAck')}</span>
            </label>

            <div className="mt-5 flex justify-end">
              <button
                type="button"
                disabled={!credentialsAcknowledged}
                onClick={() => {
                  const envId = watch('environment_id');
                  setKeycloakCredentials(null);
                  setDatabaseCredentials(null);
                  navigate(envId ? `/deployments/${envId}` : '/deployments');
                }}
                className="bg-accent-500 text-white px-5 py-2 rounded-md font-medium hover:bg-accent-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {t('deploy.keycloakCredentialsContinue')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black/50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-surface-2">
            <div className="mt-3">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100">
                <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600" />
              </div>
              <div className="mt-5 text-center">
                <h3 className="text-lg font-medium text-text-primary">
                  {t('deploy.confirmDeployTitle')}
                </h3>
                <div className="mt-4 text-sm text-text-secondary">
                  <p className="mb-2">
                    {t('deploy.confirmDeployMessage')} <strong>"{watch('app_name')}"</strong>
                  </p>
                  <p className="mb-2">
                    {t('deploy.confirmDeployType')} <strong>{appType === 'code' ? t('deploy.confirmDeployCodeType') : t('deploy.confirmDeploySaasType')}</strong>
                  </p>
                  <p className="mb-2">
                    {t('deploy.confirmDeployTemplate')} <strong>{selectedTemplate?.title}</strong>
                  </p>
                  <p className="mb-4">
                    {t('deploy.confirmDeployEnv')} <strong>{environments.find(e => e.id === watch('environment_id'))?.nom}</strong>
                  </p>
                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 text-left">
                    <p className="text-yellow-800 font-medium text-sm">
                      {t('deploy.confirmDeployWarning')}
                      {appType === 'saas' && t('deploy.confirmDeployErpNote')}
                    </p>
                  </div>
                </div>
                <div className="flex justify-center space-x-4 mt-6">
                  <button
                    onClick={() => setShowConfirmation(false)}
                    className="px-4 py-2 bg-surface-3 text-text-secondary rounded-md hover:bg-surface-3 focus:outline-none focus:ring-2 focus:ring-border-1"
                    disabled={deploying}
                  >
                    {t('cancel', { ns: 'common' })}
                  </button>
                  <button
                    onClick={confirmDeploy}
                    disabled={deploying}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {deploying ? (
                      <span className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        {t('deploy.deploying')}
                      </span>
                    ) : (
                      t('deploy.confirmDeployButton')
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quota Error Modal */}
      {quotaError && (
        <QuotaErrorModal
          isOpen={showQuotaError}
          onClose={() => {
            setShowQuotaError(false);
            setQuotaError(null);
          }}
          quotaError={quotaError}
        />
      )}
    </>
  );
};

export default DeploymentPage;